// utils/formula/formula.js
//
// The calculated-element formula language: tokenizer, parser, canonical
// serializer, static analyser (types), evaluator, and the function registry.
//
// DEPENDENCY-FREE AND PLATFORM-FREE ON PURPOSE. This file is vendored
// byte-identical into keepr-web (`src/utils/formula/formula.js`) and kept in
// lock-step by the shared vector file next to it (`vectors.json`), which both
// repos' suites drive. Nothing here may require() anything — the units
// registry the measurement arithmetic needs lives at different paths in the
// two repos, so callers INJECT it (`{ units }` in analyse/evaluate options).
// The currency module (utils/currency/, vendored the same way) is injected
// alongside it as `{ currency }`: optional, so a caller that never meets money
// keeps working, and a money formula without it fails loudly (a plain Error,
// not a FormulaError — it is the caller's bug, not the author's).
//
// Grammar (v1) — see docs/plans/calculated-elements.md in keepr-web:
//
//   expr    := or
//   or      := and ('or' and)*
//   and     := not ('and' not)*
//   not     := 'not' not | cmp
//   cmp     := concat (('=' | '!=' | '<' | '<=' | '>' | '>=') concat)?
//   concat  := add ('&' add)*
//   add     := mul (('+' | '-') mul)*
//   mul     := unary (('*' | '/' | '%') unary)*
//   unary   := '-' unary | pow
//   pow     := primary ('^' unary)?
//   primary := NUMBER [UNIT] | STRING | 'true' | 'false' | REF
//            | IDENT '(' (expr (',' expr)*)? ')' | '(' expr ')'
//   REF     := '{{' name ('.' name)* '}}'   at most `maxHops` dots: one
//                                            unless the caller says (parse)
//
// AST (in-memory; the TEXT form is what is stored):
//
//   { type: 'num', value }                 { type: 'measure', value, unit }
//   { type: 'str', value }                 { type: 'bool', value }
//   { type: 'ref', path: ['a', 'b'] }      { type: 'call', name, args: [...] }
//   { type: 'unary', op: '-' | 'not', arg }
//   { type: 'binary', op, left, right }
//
// Every node also carries a NON-ENUMERABLE `pos` (character offset) so the
// analyser can point at the offending piece without the position leaking into
// deep-equality comparisons of the AST.
//
// References are `{{tokens}}` — the platform's merge-field syntax — because
// element names carry hyphens, spaces and digits (`volume-a + volume-b` would
// read as four operands). A dot-walk `{{vehicle.weight}}` reads an element on
// the item a card-lookup points at; how far a walk may go is the CALLER's
// rule (the analyser asks `resolveRef(path)` and believes the answer).
//
// WHERE G3 WOULD GO. The one hop a ref allows today is a CARD-LOOKUP hop,
// inside one collection. Cross-collection references are deferred; when they
// land, the grammar does not move — `{{a.b}}` is already the spelling, and a
// second collection is a question for `resolveRef(path)`, which is the
// caller's rule and the only place that knows what the caller may read. The
// analyser believing the resolver is what makes that possible: a
// cross-collection hop is a resolver that answers for one more kind of
// target, not a new production here. KQL's ref stays SINGLE-segment for a
// different reason (utils/kql/kql.js) — it is evaluated against the record
// being tested, so it has no second document to read and no seam to put a
// permission check in.
//
// Types (static and runtime alike):
//   number | text | bool | date | datetime | time | measure〈measure id〉
//   money〈ISO code〉 | lookup | list〈T〉 (a multi-value lookup walk)
//   empty (a blank; unifies with any)
//
// MONEY (docs/plans/currency.md, keepr-web) is `measure`'s sibling, not one of
// its measures: there is no conversion between currencies, so where two
// measurements of one measure always combine (5 kg + 2 lb), two amounts of
// money combine only in the SAME currency. Statically a money type carries
// the codes it may hold (`currencies`: one for `5 USD` or a USD-only element,
// several for an element that allows USD and CAD, null for "any"); two known
// sets with no code in common are refused at analysis, and a runtime pair in
// different currencies warns and blanks — the measurement rule for a value
// that only turns out wrong when it is read. Runtime amounts are MINOR units
// (1250 is $12.50) and may go fractional mid-computation ($12.50 / 3); they
// are rounded ONCE, half away from zero, by toStored(). Rounding every step
// would make `{{fee}} / 2 + {{fee}} / 2` differ from `{{fee}}` by a cent.
//
// OWN UNITS (KPR-1109): an element may name units of its own (`ownUnits`: a
// pinch of nothing built in, a bale). Each is a measure of ONE unit -- its
// measure is its id, `own:<slug>` -- listed after the element's registry
// measures in `measures`. So a value in one adds, subtracts, compares and
// scales only with values in that same unit (2 smidgens x 1.5 = 3 smidgens),
// mixing it with any other unit is a runtime pair in different measures
// (blank, with a warning naming both), convert() refuses it, and toStored
// lands it only in an element that has that unit. A literal spells built-in
// units only: an own unit enters a formula through an element. An own unit is
// its ELEMENT'S, and a rename keeps its id, so two elements' own:smidgen are
// one unit only when they also NAME it alike (case and spacing aside): a value
// read from a second element whose own:smidgen another name holds makes the
// whole answer blank, with a warning, and toStored, given the target's `ownUnits`, lands an answer
// only in the target's unit of the same id AND name.
//
// SEVERAL MEASURES (KPR-1085): a measurement element may allow more than one
// measure (a recipe's amount in volume, mass or count). Its static type is a
// measure with `measures` (every measure it may be in) and no one `measure`,
// as a multi-currency element's money carries `currencies`. Two such types
// combine when they share a measure; a runtime pair in different measures
// (250 g + 1 cup) warns and blanks, money's rule. Scaling keeps the value's
// own measure, so `{{amount}} * 1.5` works for every ingredient. A
// one-measure element's type is exactly what it always was.
//
// A money LITERAL is a number followed by an upper-case ISO code: `5 USD`,
// `12.50 CAD`. It parses as the same `measure` node as `5 kg` (the tokenizer
// cannot tell a code from a unit without the registry, and a second node type
// would buy nothing: serialize is identical) and analyse/evaluate decide
// which it is. Case is the rule: `5 cup` is a volume and `5 CUP` is Cuban
// pesos; `5 kgs` is mass and `5 KGS` Kyrgyz som. Every other spelling stays
// a unit exactly as before.
//
// `time` is a WALL CLOCK ('HH:mm'), deliberately NOT unified with date or
// datetime: a clock names a recurring instant of a day and carries no zone,
// so `if(x, {{due}}, {{opens}})` must stay a type error. Clock arithmetic
// yields a `duration` MEASUREMENT (which carries its unit and converts),
// never a bare number.
//
// REPEATING GROUPS (KPR-686; epic KPE-027). A repeating group is ONE element
// whose value is an ordered list of ENTRIES, each holding the group's own
// COLUMNS (keepr-api utils/repeatingGroupValues.js: `[{ id, elements: {…} }
// | { id, section }]`, '' for none). Nothing in the grammar moves:
//
//   {{line}}         the group's ENTRIES — a type of its own (`entries`),
//                    carrying its columns' types. count() counts them (a
//                    section heading is never an entry), isEmpty() asks for
//                    none, and any() / all() test each one. Nothing else takes
//                    it: it is not a number, a text or a list of values.
//   {{line.amount}}  that column's values, one per entry, as a LIST (the
//                    multi-lookup walk's shape, so sum/avg/min/max/count read
//                    it as they read one). Which two-segment reference reads a
//                    column and which walks a lookup is the CALLER's rule, as
//                    every reference's is: describeElement(column, { list:
//                    true, fromEntries: true }) is the type it answers.
//
// An ENTRY SCOPE is where a reference reads one entry: a column's own formula
// (`entry` in analyze/evaluate's options), and the condition of
// `any({{line}}, …)` / `all({{line}}, …)` or of a total or count over the
// entries that match (below). There, {{x}} is the entry's column
// x when the group has one, else the item's element x; a name that is BOTH is
// refused at analysis (a reader cannot tell which is meant — the platform's
// never-ambiguous rule), and evaluation reads the column. A scope never sits
// inside another (one level of nesting, as the epic rules), and a dot after a
// column ({{amount.unit}}) is refused.
//
// A total over a group's column of nothing — no entries, or none filled in —
// is 0 whatever `blanks` says: the blanks choice is about the item's own
// single values, and a group with no lines has a subtotal of zero. min, max
// and avg of nothing are blank; any() of nothing is false and all() of
// nothing is true.
//
// ONLY THE ENTRIES THAT MATCH (KPR-1052). sum, avg, min and max take a
// group's column and a condition — `sum({{line.total}}, {{kind}} = "part")` —
// and count takes the group or a column and one: `count({{checklist}},
// {{result}} = "defect")`. The condition is read once per entry, in that
// entry's scope, as any()'s is; a blank answer is not a match; the matching
// entries' values are folded exactly as the whole column's would be (the rules
// for nothing above included: a total of no matches is 0). WHICH two-argument
// call is a condition is decided by syntax and the group's columns alone
// (entryCallOf, conditionNamesColumn): the first argument names a group
// ({{g.c}}, or {{g}} for count) and the second names at least one of that
// group's columns by its bare name. Anything else keeps the meaning it always
// had — `sum({{line.qty}}, {{shipping}})` adds an item element to the total,
// `count({{line}}, {{urgent}})` counts the entries and one more value — and a
// condition naming no column would ask the same of every entry anyway. Like
// any()'s, such a condition never sits inside an entry's own calculation or
// another condition: there the call is two values, as it always was, and one
// whose second value is a yes/no is refused.

'use strict';

// ---------------------------------------------------------------- constants

const MAX_FORMULA_LENGTH = 2000;
const MAX_DEPTH = 40;
const MAX_REFS = 50;

// The longest TEXT a formula may build. The three limits above bound what a
// formula SAYS; this one bounds what it MAKES. Without it, eight nested
// `replace("a", "a", "aaaaaaaaaa")` calls in a 232-character formula built a
// 100,000,000-character string — 120 ms and 96 MB for ONE evaluation, and a
// calculated element runs on every write and every recompute of its card
// (keepr-web docs/plans/analytics.md §4.3; ~/Dropbox/KEEPR/TODO.md § 0y).
//
// Every text a function or the `&` operator builds is held to it, and checked
// BEFORE the oversized string exists wherever the size is known in advance
// (replace() counts its matches; concat() and `&` add up their parts). Past
// the cap the value is blank with a warning, and the evaluation stops there:
// the FORMULA's value is blank, not just the one piece. A len() of it that
// answered 0, a contains() that answered false or an isEmpty() that answered
// true would each read like data (the rule compare() already follows for two
// currencies).
//
// A value READ from an element is not "built" and is not capped: its size is
// whatever the item already stores, and capping the read would make
// `isEmpty({{notes}})` answer true, and `len({{notes}})` 0, for a long note.
// A formula that COPIES such a value through a text function (`text()`,
// `upper()`, `&` …) builds a new text and is held to the cap like any other.
// Lengths are JavaScript string lengths: UTF-16 code units.
const MAX_TEXT_LENGTH = 10000;

// The WORK one evaluation may do, in characters its text functions scan plus
// list entries it materializes. The cap above bounds what a formula builds;
// this bounds what it READS, because a read is uncapped by design and a stored
// text element is bounded only by the 1 MB request payload. Fifty references
// to a 1,000,000-character note in one formula, each through contains(),
// number() or a shrinking replace(), scanned 50 MB for one item — a second on
// the event loop, where hapi starts shedding load. Every scan is charged
// BEFORE it runs; past the budget the same overflow path applies (the formula
// is blank, with a warning). A text is case-folded once per evaluation and
// charged once, and an identical search is answered from memory; but every
// DISTINCT search is charged its scan, because the search is the expensive
// part (a short partial match — "ab" in a long run of "a" — costs ~10 ms per
// MB; folding costs ~0.2). Two and a half million units: a 20-keyword
// classifier over a 100,000-character body (about 2.1 million) answers, and
// no function gets a second full pass over the largest note.
const MAX_WORK = 2500000;
// A list entry costs more than a character: tagging or spreading one
// allocates. Ten units each still lets one evaluation hold 200,000 entries.
const LIST_ENTRY_COST = 10;

const KEYWORDS = new Set(['and', 'or', 'not', 'true', 'false']);

const DAY_MS = 86400000;

// Display names keep the camelCase the docs use; the parser lowercases.
const FUNCTION_DISPLAY = { isempty: 'isEmpty', datediff: 'dateDiff', dateadd: 'dateAdd', datetime: 'dateTime' };

/** Own-property lookup: `constructor(1)` must not find Object.prototype. */
function functionNamed(name) {
	return Object.prototype.hasOwnProperty.call(FUNCTIONS, name) ? FUNCTIONS[name] : null;
}

// ------------------------------------------------------------------ errors

class FormulaError extends Error {
	constructor(message, position) {
		super(message);
		this.name = 'FormulaError';
		this.isFormulaError = true;
		this.position = Number.isInteger(position) ? position : 0;
	}
}

function fail(message, position) {
	throw new FormulaError(message, position);
}

function setPos(node, pos) {
	Object.defineProperty(node, 'pos', { value: pos, enumerable: false, writable: true, configurable: true });
	return node;
}

function posOf(node) {
	return node && Number.isInteger(node.pos) ? node.pos : 0;
}

// --------------------------------------------------------------- tokenizer

const NUMBER_RE = /^(?:\d+\.\d+|\d+|\.\d+)/;
// A unit spelling right after a number: letters (incl. ° and µ), digits after
// the first letter, an optional hyphenated tail (lb-oz) and an optional
// slash tail (km/h). Hyphen and slash only continue the unit when a letter
// follows, so `5 kg-{{x}}` and `10 km/{{h}}` still read as arithmetic.
const UNIT_RE = /^[A-Za-z°µ][A-Za-z0-9°µ]*(?:-[A-Za-z][A-Za-z0-9]*)*(?:\/[A-Za-z][A-Za-z0-9]*)?/;
const IDENT_RE = /^[A-Za-z_][A-Za-z0-9_]*/;
const REF_RE = /^\{\{([^{}]*)\}\}/; // no nested quantifiers: linear on unclosed input
// Names that would reach Object.prototype through a plain-object element
// map on the caller's side. Refused as reference segments outright — the
// module is the shared choke point for both callers.
const RESERVED_SEGMENTS = new Set(['__proto__', 'constructor', 'prototype']);
const OPERATORS = ['<=', '>=', '!=', '<>', '+', '-', '*', '/', '%', '^', '&', '=', '<', '>', '(', ')', ','];

function tokenize(text) {
	if (typeof text !== 'string') fail('The formula must be text.', 0);
	if (text.length > MAX_FORMULA_LENGTH) fail(`The formula is too long (limit ${MAX_FORMULA_LENGTH} characters).`, MAX_FORMULA_LENGTH);
	const tokens = [];
	let i = 0;
	while (i < text.length) {
		const ch = text[i];
		if (/\s/.test(ch)) { i++; continue; }
		const rest = text.slice(i);

		if (rest.startsWith('{{')) {
			const m = rest.match(REF_RE);
			if (!m) fail('Unclosed {{ reference.', i);
			const inner = m[1].trim();
			if (!inner) fail('Empty {{ }} reference.', i);
			const path = inner.split('.').map(s => s.trim());
			if (path.some(s => !s)) fail(`"{{${inner}}}" is not a valid reference.`, i);
			if (path.some(s => RESERVED_SEGMENTS.has(s))) fail(`"{{${inner}}}" is not a valid reference.`, i);
			tokens.push({ kind: 'ref', path, pos: i });
			i += m[0].length;
			continue;
		}
		if (rest.startsWith('}}')) fail('Unexpected }} — every reference must start with {{.', i);

		if (ch === '"' || ch === '\'') {
			const quote = ch;
			let j = i + 1;
			let value = '';
			let closed = false;
			while (j < text.length) {
				const c = text[j];
				if (c === '\\' && j + 1 < text.length && (text[j + 1] === '\\' || text[j + 1] === quote)) { value += text[j + 1]; j += 2; continue; }
				if (c === quote) { closed = true; j++; break; }
				value += c;
				j++;
			}
			if (!closed) fail('Unclosed text — add the closing quote.', i);
			tokens.push({ kind: 'str', value, pos: i });
			i = j;
			continue;
		}

		const num = rest.match(NUMBER_RE);
		if (num) {
			const token = { kind: 'num', value: Number(num[0]), pos: i };
			if (!Number.isFinite(token.value)) fail('The number is too large.', i);
			i += num[0].length;
			// An optional unit: `5 kg`, `2.5lb`. Never a keyword, never a name
			// that is immediately called like a function.
			const after = text.slice(i).match(/^\s*/)[0].length;
			const unitMatch = text.slice(i + after).match(UNIT_RE);
			if (unitMatch) {
				const spelling = unitMatch[0];
				const followedByParen = /^\s*\(/.test(text.slice(i + after + spelling.length));
				if (!KEYWORDS.has(spelling.toLowerCase()) && !followedByParen) {
					token.unit = spelling;
					i += after + spelling.length;
				}
			}
			tokens.push(token);
			continue;
		}

		const ident = rest.match(IDENT_RE);
		if (ident) {
			const word = ident[0];
			const lower = word.toLowerCase();
			if (KEYWORDS.has(lower)) tokens.push({ kind: 'kw', value: lower, pos: i });
			else tokens.push({ kind: 'ident', value: word, pos: i });
			i += word.length;
			continue;
		}

		const op = OPERATORS.find(candidate => rest.startsWith(candidate));
		if (op) {
			tokens.push({ kind: 'op', value: op === '<>' ? '!=' : op, pos: i });
			i += op.length;
			continue;
		}

		fail(`Unexpected character "${ch}".`, i);
	}
	tokens.push({ kind: 'eof', pos: text.length });
	return tokens;
}

// ------------------------------------------------------------------ parser

/**
 * parse(text, { maxHops }) -> AST, or throws a FormulaError.
 *
 * `maxHops` is how many card-lookup hops one reference may follow: `{{a.b}}`
 * follows one, `{{a.b.c.d}}` three. It defaults to ONE, the rule of every
 * surface that reads a formula today (calculated elements, group columns,
 * charts, quick add), and the default keeps that sentence word for word. A
 * caller whose resolver follows more passes its own cap — a card change
 * passes three, KQL's (keepr-api utils/changeCard/compose.js MAX_HOPS,
 * KPR-701) — and its resolver still decides what each hop reads and whether
 * the caller may read it. A cap that is not a whole number of at least one is
 * the caller's bug: a plain Error, not a FormulaError.
 */
function parse(text, { maxHops = 1 } = {}) {
	if (!Number.isInteger(maxHops) || maxHops < 1) throw new Error('parse: maxHops must be a whole number of at least 1.');
	const tokens = tokenize(text);
	let index = 0;
	let depth = 0;
	let refCount = 0;

	const peek = () => tokens[index];
	const next = () => tokens[index++];
	const isOp = (value) => peek().kind === 'op' && peek().value === value;
	const isKw = (value) => peek().kind === 'kw' && peek().value === value;
	const expectOp = (value, what) => {
		if (!isOp(value)) {
			const tok = peek();
			fail(`Expected ${what || `"${value}"`}${tok.kind === 'eof' ? ' before the end of the formula' : ''}.`, tok.pos);
		}
		return next();
	};

	const enter = (pos) => {
		depth++;
		if (depth > MAX_DEPTH) fail('The formula is nested too deeply.', pos);
	};
	const leave = () => { depth--; };

	function parseOr() {
		let left = parseAnd();
		while (isKw('or')) {
			const tok = next();
			const right = parseAnd();
			left = setPos({ type: 'binary', op: 'or', left, right }, tok.pos);
		}
		return left;
	}
	function parseAnd() {
		let left = parseNot();
		while (isKw('and')) {
			const tok = next();
			const right = parseNot();
			left = setPos({ type: 'binary', op: 'and', left, right }, tok.pos);
		}
		return left;
	}
	function parseNot() {
		if (isKw('not')) {
			const tok = next();
			enter(tok.pos);
			const arg = parseNot();
			leave();
			return setPos({ type: 'unary', op: 'not', arg }, tok.pos);
		}
		return parseCmp();
	}
	function parseCmp() {
		const left = parseConcat();
		if (peek().kind === 'op' && ['=', '!=', '<', '<=', '>', '>='].includes(peek().value)) {
			const tok = next();
			const right = parseConcat();
			const node = setPos({ type: 'binary', op: tok.value, left, right }, tok.pos);
			if (peek().kind === 'op' && ['=', '!=', '<', '<=', '>', '>='].includes(peek().value)) {
				fail('Comparisons cannot be chained — combine them with "and".', peek().pos);
			}
			return node;
		}
		return left;
	}
	function parseConcat() {
		let left = parseAdd();
		while (isOp('&')) {
			const tok = next();
			const right = parseAdd();
			left = setPos({ type: 'binary', op: '&', left, right }, tok.pos);
		}
		return left;
	}
	function parseAdd() {
		let left = parseMul();
		while (isOp('+') || isOp('-')) {
			const tok = next();
			const right = parseMul();
			left = setPos({ type: 'binary', op: tok.value, left, right }, tok.pos);
		}
		return left;
	}
	function parseMul() {
		let left = parseUnary();
		while (isOp('*') || isOp('/') || isOp('%')) {
			const tok = next();
			const right = parseUnary();
			left = setPos({ type: 'binary', op: tok.value, left, right }, tok.pos);
		}
		return left;
	}
	function parseUnary() {
		if (isOp('-')) {
			const tok = next();
			enter(tok.pos);
			const arg = parseUnary();
			leave();
			// `-5` is a literal, not a negation of a literal — canonical text
			// and the evaluator both prefer the plain number.
			if (arg.type === 'num') return setPos({ type: 'num', value: -arg.value }, tok.pos);
			if (arg.type === 'measure') return setPos({ type: 'measure', value: -arg.value, unit: arg.unit }, tok.pos);
			return setPos({ type: 'unary', op: '-', arg }, tok.pos);
		}
		return parsePow();
	}
	function parsePow() {
		const base = parsePrimary();
		if (isOp('^')) {
			const tok = next();
			enter(tok.pos);
			const exponent = parseUnary(); // right-associative: 2 ^ 3 ^ 2 = 2 ^ (3 ^ 2)
			leave();
			return setPos({ type: 'binary', op: '^', left: base, right: exponent }, tok.pos);
		}
		return base;
	}
	function parsePrimary() {
		const tok = peek();
		if (tok.kind === 'num') {
			next();
			if (tok.unit) return setPos({ type: 'measure', value: tok.value, unit: tok.unit }, tok.pos);
			return setPos({ type: 'num', value: tok.value }, tok.pos);
		}
		if (tok.kind === 'str') { next(); return setPos({ type: 'str', value: tok.value }, tok.pos); }
		if (tok.kind === 'kw' && (tok.value === 'true' || tok.value === 'false')) {
			next();
			return setPos({ type: 'bool', value: tok.value === 'true' }, tok.pos);
		}
		if (tok.kind === 'ref') {
			next();
			refCount++;
			if (refCount > MAX_REFS) fail(`Too many references (limit ${MAX_REFS}).`, tok.pos);
			if (tok.path.length > maxHops + 1) {
				fail(maxHops === 1
					? `"{{${tok.path.join('.')}}}" walks too far — one lookup hop is the limit.`
					: `"{{${tok.path.join('.')}}}" walks too far — ${maxHops} lookup hops are the limit.`, tok.pos);
			}
			return setPos({ type: 'ref', path: tok.path }, tok.pos);
		}
		if (tok.kind === 'ident') {
			next();
			if (!isOp('(')) {
				if (functionNamed(tok.value.toLowerCase())) fail(`${FUNCTION_DISPLAY[tok.value.toLowerCase()] || tok.value.toLowerCase()}() needs parentheses around its values.`, tok.pos);
				fail(`"${tok.value}" is not a function — element names go inside {{ }}.`, tok.pos);
			}
			next();
			enter(tok.pos);
			const args = [];
			if (!isOp(')')) {
				args.push(parseOr());
				while (isOp(',')) { next(); args.push(parseOr()); }
			}
			expectOp(')', '")" to close the function call');
			leave();
			return setPos({ type: 'call', name: tok.value.toLowerCase(), args }, tok.pos);
		}
		if (tok.kind === 'op' && tok.value === '(') {
			next();
			enter(tok.pos);
			const inner = parseOr();
			expectOp(')', '")"');
			leave();
			return inner;
		}
		if (tok.kind === 'eof') fail('The formula ends too soon — something is missing.', tok.pos);
		if (tok.kind === 'op' && tok.value === ')') fail('Unexpected ")".', tok.pos);
		if (tok.kind === 'kw') fail(`Unexpected "${tok.value}".`, tok.pos);
		fail(`Unexpected "${tok.value}".`, tok.pos);
		return null;
	}

	if (peek().kind === 'eof') fail('The formula is empty.', 0);
	const ast = parseOr();
	if (peek().kind !== 'eof') {
		const tok = peek();
		if (tok.kind === 'op' && tok.value === ')') fail('Unexpected ")" — there is no matching "(".', tok.pos);
		fail(`Unexpected ${tok.kind === 'ref' ? 'reference' : `"${tok.value}"`} — an operator is missing before it.`, tok.pos);
	}
	return ast;
}

// -------------------------------------------------------------- serializer

const PRECEDENCE = { or: 1, and: 2, not: 3, cmp: 4, '&': 5, '+': 6, '-': 6, '*': 7, '/': 7, '%': 7, neg: 8, '^': 9 };
const CMP_OPS = new Set(['=', '!=', '<', '<=', '>', '>=']);

function precedenceOf(node) {
	if (node.type === 'binary') return CMP_OPS.has(node.op) ? PRECEDENCE.cmp : PRECEDENCE[node.op];
	if (node.type === 'unary') return node.op === 'not' ? PRECEDENCE.not : PRECEDENCE.neg;
	return 100;
}

function quote(value) {
	return `"${String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/** Plain decimal text, never exponent form (the tokenizer would read the
 * `e` of `1e-7` as a unit and the stored formula would be unreadable). */
function serializeNumber(n) {
	if (!Number.isFinite(n)) return '0';
	const text = String(n);
	if (!/e/i.test(text)) return text;
	const [mantissa, exponentText] = text.toLowerCase().split('e');
	const exponent = Number(exponentText);
	const negative = mantissa.startsWith('-');
	const digits = mantissa.replace('-', '').replace('.', '');
	const pointAt = (mantissa.replace('-', '').split('.')[0] || '').length + exponent;
	let out;
	if (pointAt <= 0) out = `0.${'0'.repeat(-pointAt)}${digits}`;
	else if (pointAt >= digits.length) out = `${digits}${'0'.repeat(pointAt - digits.length)}`;
	else out = `${digits.slice(0, pointAt)}.${digits.slice(pointAt)}`;
	out = out.replace(/^0+(?=\d)/, '');
	return negative ? `-${out}` : out;
}

/** Canonical text. `parse(serialize(parse(x)))` is stable for every input. */
function serialize(node) {
	switch (node.type) {
		case 'num': return serializeNumber(node.value);
		case 'measure': return `${serializeNumber(node.value)} ${node.unit}`;
		case 'str': return quote(node.value);
		case 'bool': return node.value ? 'true' : 'false';
		case 'ref': return `{{${node.path.join('.')}}}`;
		case 'call': return `${FUNCTION_DISPLAY[node.name] || node.name}(${node.args.map(serialize).join(', ')})`;
		case 'unary': {
			const inner = serialize(node.arg);
			const needs = precedenceOf(node.arg) < precedenceOf(node);
			return node.op === 'not'
				? `not ${needs ? `(${inner})` : inner}`
				: `-${needs ? `(${inner})` : inner}`;
		}
		case 'binary': {
			const prec = precedenceOf(node);
			const wrap = (child, rightSide) => {
				const childPrec = precedenceOf(child);
				// Left-associative operators need parens on the right when
				// equal precedence (a - (b - c)); the right-associative ^ on
				// the left ((a ^ b) ^ c). Comparisons never nest unparenthesised.
				const negativeLiteral = (child.type === 'num' || child.type === 'measure') && child.value < 0;
				const needs = childPrec < prec
					|| (childPrec === prec && (node.op === '^' ? !rightSide : rightSide))
					|| (CMP_OPS.has(node.op) && CMP_OPS.has(child.op))
					// (-2) ^ 2 is 4; -2 ^ 2 would re-parse as -(2 ^ 2).
					|| (node.op === '^' && !rightSide && negativeLiteral);
				const text = serialize(child);
				return needs ? `(${text})` : text;
			};
			return `${wrap(node.left, false)} ${node.op} ${wrap(node.right, true)}`;
		}
		default:
			throw new FormulaError(`Unknown node type "${node.type}".`, 0);
	}
}

// ----------------------------------------------------------------- walking

/** Every reference in document order: [{ path, node }]. */
function references(node, out = []) {
	if (!node) return out;
	if (node.type === 'ref') out.push({ path: node.path.slice(), node });
	if (node.type === 'call') node.args.forEach(arg => references(arg, out));
	if (node.type === 'unary') references(node.arg, out);
	if (node.type === 'binary') { references(node.left, out); references(node.right, out); }
	return out;
}

/**
 * Every reference in document order, with the any()/all() scope it is read in
 * (KPR-686): `[{ path, node, scope }]`, `scope` the group named by the
 * condition's {{group}} (null outside a condition, and for that {{group}}
 * itself). Syntax only — whether a scoped name is the entry's column or the
 * item's element is the caller's to decide with the group's columns (a column
 * first). A column's own formula is one scope throughout, which the caller
 * knows without asking.
 *
 * The second argument of a two-argument sum/avg/min/max/count over a group
 * (KPR-1052, entryCallOf) is scoped the same way whether or not it turns out
 * to be a condition: column first is the right reading either way, because it
 * is a condition exactly when it names one of the group's columns. Inside a
 * condition such a call opens no scope of its own (it is two values there).
 *
 * `isColumn(group, name)` (optional): true when `name` is a column of the
 * repeating group `group` (false when `group` is no group). Given it, an
 * aggregate's second argument is scoped exactly when analyze reads it as a
 * condition — which matters when one sits inside another's second value:
 * `sum({{a.x}}, sum({{b.y}}, {{k}} = 1))` with `k` a column of b alone is the
 * outer total plus b's matching entries, and `k` is b's. A caller deciding
 * what a formula reads (a hidden column, a dependency) passes it.
 */
function scopedReferences(node, out = [], scope = null, isColumn = null) {
	if (!node) return out;
	if (node.type === 'ref') out.push({ path: node.path.slice(), node, scope });
	if (node.type === 'call') {
		const call = entryCallOf(node);
		// Inside a condition no other opens (one level): an aggregate there is
		// two values, read in the scope it sits in.
		const opens = !!call && (!call.aggregate || (scope === null
			&& (typeof isColumn !== 'function' || references(call.condition).some(ref => isColumn(call.group, ref.path[0])))));
		if (opens) {
			scopedReferences(node.args[0], out, scope, isColumn);
			scopedReferences(call.condition, out, call.group, isColumn);
		} else {
			node.args.forEach(arg => scopedReferences(arg, out, scope, isColumn));
		}
	}
	if (node.type === 'unary') scopedReferences(node.arg, out, scope, isColumn);
	if (node.type === 'binary') { scopedReferences(node.left, out, scope, isColumn); scopedReferences(node.right, out, scope, isColumn); }
	return out;
}

// The aggregates that take a condition over a group's entries (KPR-1052).
const ENTRY_AGGREGATES = new Set(['sum', 'avg', 'min', 'max', 'count']);

/**
 * The call that asks about a group's entries one by one — syntax only
 * (KPR-686, KPR-1052): `{ group, column, condition, aggregate }`, or null.
 *
 *   any({{g}}, cond) / all({{g}}, cond)          column null, aggregate false
 *   sum({{g.c}}, x)  avg / min / max the same    column c,    aggregate true
 *   count({{g}}, x) / count({{g.c}}, x)          column null / c
 *
 * For any()/all() the second argument IS the condition. For an aggregate it
 * is one only when `g` is a repeating group and `x` names one of its columns
 * (conditionNamesColumn) — the reader with the group's columns decides; a
 * head that is a lookup (`{{vendor.price}}`) is no group, and the caller's
 * resolver says so. A caller pricing or walking names may treat every shape
 * here as scoped: column first reads the same names either way.
 */
function entryCallOf(node) {
	if (!node || node.type !== 'call' || !Array.isArray(node.args) || node.args.length !== 2) return null;
	const first = node.args[0];
	if (!first || first.type !== 'ref' || !Array.isArray(first.path)) return null;
	const fn = functionNamed(node.name);
	if (fn && fn.scoped) return first.path.length === 1 ? { group: first.path[0], column: null, condition: node.args[1], aggregate: false } : null;
	if (!ENTRY_AGGREGATES.has(node.name)) return null;
	if (first.path.length === 2) return { group: first.path[0], column: first.path[1], condition: node.args[1], aggregate: true };
	if (first.path.length === 1 && node.name === 'count') return { group: first.path[0], column: null, condition: node.args[1], aggregate: true };
	return null;
}

/** Does `node` name, by its bare name, one of `columns` (a group's column map)? A dotted {{column.x}} counts: it is a mistake the condition's own analysis names. */
function conditionNamesColumn(node, columns) {
	return references(node).some(ref => !!columnOf(columns, ref.path[0]));
}

/** Does evaluation depend on the clock (today/now, or age without an as-of)? */
function isTimeDependent(node) {
	if (!node) return false;
	if (node.type === 'call') {
		if (node.name === 'today' || node.name === 'now') return true;
		if (node.name === 'age' && node.args.length < 2) return true;
		return node.args.some(isTimeDependent);
	}
	if (node.type === 'unary') return isTimeDependent(node.arg);
	if (node.type === 'binary') return isTimeDependent(node.left) || isTimeDependent(node.right);
	return false;
}

// ------------------------------------------------------------------- types
//
// A static type is { type, measure?, item? } where `item` is the element type
// of a list. Element dataTypes map through describeElement().

const T = {
	number: () => ({ type: 'number' }),
	text: () => ({ type: 'text' }),
	bool: () => ({ type: 'bool' }),
	date: () => ({ type: 'date' }),
	datetime: () => ({ type: 'datetime' }),
	time: () => ({ type: 'time' }),
	// `measures` (KPR-1085): every measure the value may be in when that is
	// more than one, `measure` then null. A list of one is that measure.
	measure: (measure, measures) => {
		const list = !measure && Array.isArray(measures) ? measures.filter((m, i) => typeof m === 'string' && measures.indexOf(m) === i) : null;
		if (list && list.length > 1) return { type: 'measure', measure: null, measures: list };
		return { type: 'measure', measure: measure || (list && list.length === 1 ? list[0] : null) };
	},
	// `currency` is the ONE code when only one is possible, else null;
	// `currencies` every code the value may hold (null = unknown, any).
	money: (currencies) => {
		const list = Array.isArray(currencies) && currencies.length ? currencies.slice() : null;
		return { type: 'money', currency: list && list.length === 1 ? list[0] : null, currencies: list };
	},
	lookup: () => ({ type: 'lookup' }),
	list: (item) => ({ type: 'list', item }),
	// A repeating group's entries (KPR-686): `columns` maps each column's name
	// to its own type (no prototype: a column named `toString` is a column),
	// `group` names the group for the sentences.
	entries: (columns, group) => ({ type: 'entries', columns: columns || Object.create(null), group: group || null }),
	empty: () => ({ type: 'empty' }),
};

/** Own-property read of a column map (no prototype is ever consulted). */
function columnOf(columns, name) {
	return columns && Object.prototype.hasOwnProperty.call(columns, name) ? columns[name] : null;
}

const NUMERIC_TYPES = new Set(['number', 'decimal', 'integer', 'rating']);
// `phone`, `email` and `location` are TEXT to the language: a phone number
// concatenates, an address matches, and neither has arithmetic. A `location`
// stores an OBJECT, so toTagged() renders it to its address before the text
// rules see it (locationText below). A `color` is its "#rrggbb" text.
const TEXT_TYPES = new Set(['text-small', 'text-large', 'rich-text', 'choice', 'url', 'phone', 'email', 'location', 'color']);
// The types a formula CANNOT read. A `file` value is an attachment id of its
// own item (keepr-web docs/plans/data-types-file-element.md): as text it is
// noise, and nothing a formula computes could use it. describeElement marks
// such an element `unreadable`, and analyze refuses the reference by name.
// A `confirmations` count (KPR-390) changes when somebody says "me too",
// without its item being saved — so a calculated element reading it would
// hold a stale number until the next save, and nothing would say so.
const UNREADABLE_TYPES = new Set(['file', 'confirmations']);

// The text a stored `location` reads as: its address, or its coordinates to
// five decimals when there is no address.
//
// A DELIBERATE SIX-LINE DUPLICATE of utils/elementFormat/location.js
// `formatLocation`, which is this rule's authority. This file may not
// require() anything (see the header): the two repos put that module at
// different paths, and the whole point of vendoring formula.js byte-identical
// is that it has no edges. Six lines of duplication is the cost; if the
// rendering rule ever changes, it changes in both files and the vector suites
// on both sides say so.
function locationText(raw) {
	if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
	const address = typeof raw.address === 'string' ? raw.address.trim() : '';
	if (address) return address;
	const finite = v => typeof v === 'number' && isFinite(v);
	if (!finite(raw.lat) || !finite(raw.lng)) return '';
	return `${Number(raw.lat.toFixed(5))}, ${Number(raw.lng.toFixed(5))}`;
}

/**
 * The formula type of a card element definition ({ dataType, options }).
 * `{ list: true }` wraps it for a multi-value lookup walk; unknown dataTypes
 * read as text (the platform's fall-through rendering) — but a caller that
 * wants to fail closed can check `describeElement(el).unknown`. A `boolean`
 * element is the language's own `bool`, so `{{shipped}} and {{paid}}` reads
 * and writes without a detour through "true"/"false" text. `phone`, `email`
 * and `location` are text — the last one as its address.
 */
function describeElement(el, { list = false, fromEntries = false } = {}) {
	const dataType = el && el.dataType;
	const options = (el && el.options) || {};
	let t;
	let unknown = false;
	let unreadable = false;
	if (UNREADABLE_TYPES.has(dataType)) { t = T.text(); unreadable = true; }
	// A repeating group (KPR-686): its entries, with each column's own type.
	else if (dataType === REPEATING_GROUP) t = T.entries(columnTypesOf(options), el && typeof el.name === 'string' ? el.name : null);
	else if (NUMERIC_TYPES.has(dataType)) t = T.number();
	else if (TEXT_TYPES.has(dataType)) t = T.text();
	// (`t.dataType` below is what tells toTagged a text value arrived as a
	// location object rather than as a string.)
	else if (dataType === 'boolean') t = T.bool();
	else if (dataType === 'date') t = T.date();
	else if (dataType === 'date-time') t = T.datetime();
	else if (dataType === 'time') t = T.time();
	else if (dataType === 'measurement') {
		const several = measuresOfOptions(options);
		t = several && several.length > 1 ? T.measure(null, several) : T.measure(options.measure || null);
	}
	else if (dataType === 'currency') t = T.money(currencyListOf(options));
	else if (dataType === 'card-lookup') t = options.allowMultiple ? T.list(T.lookup()) : T.lookup();
	else { t = T.text(); unknown = true; }
	const out = list && t.type !== 'list' ? T.list(t) : t;
	out.options = options;
	out.dataType = dataType;
	if (unknown) out.unknown = true;
	if (unreadable) out.unreadable = true;
	// A list of a repeating group's column, one value per entry (KPR-686): a
	// total of it over nothing is 0 whatever `blanks` says (the header) — in
	// the column's own default unit or currency, which the item type carries
	// for it (a lookup walk's list keeps its own rule).
	if (list && fromEntries && out !== t) {
		out.fromEntries = true;
		t.options = options;
		t.dataType = dataType;
	}
	return out;
}

const REPEATING_GROUP = 'repeating-group';

/**
 * A group's columns as a map of name -> type. A column that is itself a group
 * (never stored: one level of nesting) or that has no name is left out.
 */
function columnTypesOf(options) {
	const out = Object.create(null);
	const columns = options && Array.isArray(options.columns) ? options.columns : [];
	for (let i = 0; i < columns.length; i++) {
		const column = columns[i];
		if (!column || typeof column !== 'object' || typeof column.name !== 'string' || !column.name) continue;
		if (column.dataType === REPEATING_GROUP || RESERVED_SEGMENTS.has(column.name)) continue;
		out[column.name] = describeElement(column);
	}
	return out;
}

/**
 * The ENTRIES of a stored group value (KPR-686): every entry that holds
 * column values, in order — a section heading, a malformed member or a value
 * that is not a list holds none. What a formula reads; the stored shape is
 * keepr-api utils/repeatingGroupValues.js's.
 */
function entriesOf(raw) {
	if (!Array.isArray(raw)) return [];
	const out = [];
	for (let i = 0; i < raw.length; i++) {
		const e = raw[i];
		if (!e || typeof e !== 'object' || Array.isArray(e) || typeof e.section === 'string') continue;
		if (!e.elements || typeof e.elements !== 'object' || Array.isArray(e.elements)) continue;
		out.push(e);
	}
	return out;
}

/** One column's values across a group's entries (`{{line.amount}}`), in order; a blank where an entry holds none. */
function columnValuesOf(raw, column) {
	return entriesOf(raw).map(e => (Object.prototype.hasOwnProperty.call(e.elements, column) ? e.elements[column] : undefined));
}

/**
 * The codes a currency element allows, as written (upper-case strings, de-
 * duplicated), or null when it names none. Not filtered against the registry:
 * this runs without one (describeElement takes no injection), and the card
 * validator already refuses an unknown code.
 */
function currencyListOf(options) {
	const list = options && Array.isArray(options.currencies) ? options.currencies : [];
	const out = [];
	list.forEach(code => { if (typeof code === 'string' && code && out.indexOf(code) === -1) out.push(code); });
	return out.length ? out : null;
}

/** Codes both money types may hold; null = any. [] means none in common. */
function currencyMeet(a, b) {
	if (!a.currencies) return b.currencies;
	if (!b.currencies) return a.currencies;
	return a.currencies.filter(code => b.currencies.indexOf(code) !== -1);
}
/** Codes either may hold (if() / coalesce() branches); null = any. */
function currencyJoin(a, b) {
	if (!a.currencies || !b.currencies) return null;
	const out = a.currencies.slice();
	b.currencies.forEach(code => { if (out.indexOf(code) === -1) out.push(code); });
	return out;
}

/**
 * The measures a measurement element's options allow, the primary first
 * (KPR-1085) -- the units module's allowedMeasures read without the registry
 * (this file requires nothing): `measures` counts only as a list naming the
 * primary `measure`. Null with no measure.
 */
function measuresOfOptions(options) {
	const primary = options && typeof options.measure === 'string' && options.measure ? options.measure : null;
	if (!primary) return null;
	const list = Array.isArray(options.measures) ? options.measures : null;
	const out = [primary];
	if (list && list.indexOf(primary) !== -1) list.forEach(m => { if (typeof m === 'string' && m && out.indexOf(m) === -1) out.push(m); });
	// An element's OWN units (KPR-1109): each a measure of one unit, after the
	// registry's -- the units module's allowedMeasures, read without it.
	ownUnitsOfOptions(options).forEach(own => { if (out.indexOf(own.id) === -1) out.push(own.id); });
	return out;
}

// ---------------------------------------------------------------- own units
//
// An element's OWN units (KPR-1109, `options.ownUnits`: [{ id, singular,
// plural }], the id `own:<slug>`): units its author named, which behave as
// counts. A value in one is a measure value whose measure IS the unit's id, so
// it adds, subtracts, compares and scales only with values in that same unit
// (measuresDiffer below), and converts to nothing. Read here without the units
// registry, as measuresOfOptions reads `measures`.
const OWN_PREFIX = 'own:';
function isOwnUnitId(id) {
	return typeof id === 'string' && id.length > OWN_PREFIX.length && id.slice(0, OWN_PREFIX.length) === OWN_PREFIX;
}
/** The own units an element's options name, each { id, singular, plural }; malformed entries skipped. */
function ownUnitsOfOptions(options) {
	const list = options && Array.isArray(options.ownUnits) ? options.ownUnits : [];
	const out = [];
	list.forEach(entry => {
		if (!entry || typeof entry !== 'object' || !isOwnUnitId(entry.id) || typeof entry.singular !== 'string' || !entry.singular.trim()) return;
		if (out.some(own => own.id === entry.id)) return;
		const singular = entry.singular.trim();
		out.push({ id: entry.id, singular, plural: typeof entry.plural === 'string' && entry.plural.trim() ? entry.plural.trim() : singular });
	});
	return out;
}
/** The key two own unit names are told apart by: spaces collapsed, composed, lower-cased (the units registry's ownNameKey). */
function ownNameKeyOf(name) {
	const text = String(name === undefined || name === null ? '' : name).replace(/\s+/g, ' ').trim();
	return (typeof text.normalize === 'function' ? text.normalize('NFC') : text).toLowerCase();
}
/** A measure id in words: a registry measure's id, an own unit's plural (known to `rt`) or its id's words. */
function measureWord(id, rt) {
	if (!isOwnUnitId(id)) return id;
	const own = rt && rt.ownUnits && rt.ownUnits[id];
	return own ? own.plural : id.slice(OWN_PREFIX.length).replace(/-/g, ' ');
}

/** The measures a measure type may be in: [measure], its `measures`, or null (any). */
function measureSetOf(t) {
	if (t.measure) return [t.measure];
	return Array.isArray(t.measures) ? t.measures : null;
}
/** The measures two types may BOTH be in: a side that may be any takes the other's; null when both may be any. */
function measureMeet(a, b) {
	const sa = measureSetOf(a);
	const sb = measureSetOf(b);
	if (!sa) return sb;
	if (!sb) return sa;
	return sa.filter(m => sb.indexOf(m) !== -1);
}
/** The measures EITHER of two types may be in: null (any) when either may be any. */
function measureJoin(a, b) {
	const sa = measureSetOf(a);
	const sb = measureSetOf(b);
	if (!sa || !sb) return null;
	const out = sa.slice();
	sb.forEach(m => { if (out.indexOf(m) === -1) out.push(m); });
	return out;
}
/** "volume, mass or count" (an own unit in words, KPR-1109: "volume or smidgen") */
function measureListWords(list) {
	const words = list.map(id => measureWord(id));
	return words.length < 2 ? (words[0] || '') : `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`;
}

function typeLabel(t) {
	if (!t) return 'unknown';
	if (t.type === 'measure') return t.measure ? `${measureWord(t.measure)} measurement` : (t.measures ? `${measureListWords(t.measures)} measurement` : 'measurement');
	if (t.type === 'money') {
		const list = t.currencies || [];
		if (!list.length) return 'money';
		if (list.length === 1) return `${list[0]} money`;
		return `${list.slice(0, -1).join(', ')} or ${list[list.length - 1]} money`;
	}
	if (t.type === 'list') return `list of ${typeLabel(t.item)}`;
	if (t.type === 'datetime') return 'date-time';
	if (t.type === 'entries') return 'entries';
	return t.type;
}

function sameType(a, b) {
	if (a.type !== b.type) return false;
	// Some measure possible on both sides (KPR-1085: a list against a list, or
	// one measure); a side that may be any compares with everything.
	if (a.type === 'measure') { const meet = measureMeet(a, b); return !meet || meet.length > 0; }
	// Money is the same type when SOME currency is possible on both sides:
	// a USD-or-CAD element against a USD literal compares (a CAD row is a
	// runtime warning), a USD element against a CAD one never can.
	if (a.type === 'money') { const meet = currencyMeet(a, b); return !meet || meet.length > 0; }
	if (a.type === 'list') return sameType(a.item, b.item);
	return true;
}

/** The common type of two branches; 'empty' yields to the other side. */
function unify(a, b, pos, what) {
	if (a.type === 'empty') return b;
	if (b.type === 'empty') return a;
	if (isDateish(a) && isDateish(b)) return a.type === 'datetime' || b.type === 'datetime' ? T.datetime() : T.date();
	if (sameType(a, b)) {
		if (a.type === 'measure') {
			// Either branch's value: the measures of both (KPR-1085).
			if (!a.measures && !b.measures) return T.measure(a.measure || b.measure);
			const join = measureJoin(a, b);
			return join ? T.measure(null, join) : T.measure(null);
		}
		if (a.type === 'money') return T.money(currencyJoin(a, b));
		return a;
	}
	fail(`${what}: the branches have different types (${typeLabel(a)} and ${typeLabel(b)}).`, pos);
	return null;
}

const isDateish = t => t.type === 'date' || t.type === 'datetime';
// DELIBERATELY SEPARATE from isDateish. isDateish drives unify(), so folding
// `time` into it would make `if(x, {{due}}, {{opens}})` legal — a date and a
// wall clock are not two spellings of one thing.
const isClockish = t => t.type === 'time';
// The one measure clock arithmetic produces and consumes. A bare string
// constant on purpose: this file may not require() the units registry, and
// an injected registry that has never heard of 'duration' must degrade to a
// measurement with no conversions rather than throw.
const DURATION = 'duration';
const isDurationish = t => t.type === 'measure' && t.measure === DURATION;

// An affine measure (temperature: °F, K) has a base offset, so adding or
// scaling bases is meaningless; only differences, comparisons, min/max and
// averages are. Detected from the registry: a unit whose zero is not the
// base's zero. Fuel economy is one too (units PR 8): L/100km is a reciprocal,
// whose zero is no base at all (Infinity), and 30 mpg + 30 mpg is not 60 of
// anything; a difference reads in the left operand's unit, as degrees do.
const affineCache = new Map();
function isAffineMeasure(measureId, units) {
	if (!measureId || !units) return false;
	if (affineCache.has(measureId)) return affineCache.get(measureId);
	const measure = units.getMeasure(measureId);
	let affine = false;
	if (measure) {
		for (const unitId of measure.units || []) {
			const unit = units.getUnit(unitId);
			try { if (unit && Math.abs(unit.toBase(0)) > 1e-9) { affine = true; break; } } catch (e) { /* ignore */ }
		}
	}
	affineCache.set(measureId, affine);
	return affine;
}

// How a refusal names an affine measure: temperature keeps the words it
// always had; any other says its registry label ("Fuel economy values",
// "a fuel economy").
function affineName(measureId, units) {
	if (measureId === 'temperature') return { plural: 'Temperatures', one: 'a temperature' };
	const measure = units && measureId ? units.getMeasure(measureId) : null;
	const label = measure && typeof measure.label === 'string' ? measure.label : 'Measurement';
	return { plural: label + ' values', one: 'a ' + label.toLowerCase() };
}

// --------------------------------------------------------------- functions
//
// Each entry: args (for the reference UI), `check(argTypes, ctx) -> type`
// (throws FormulaError via ctx.fail), `apply(values, rt) -> tagged value`.
// Runtime tagged values: { t: 'number', v } | { t: 'text', v } | { t: 'bool', v }
// | { t: 'date', v: 'YYYY-MM-DD' } | { t: 'datetime', v: ISO } |
// { t: 'time', v: 'HH:mm' | 'HH:mm:ss' } |
// { t: 'measure', measure, base, unit } |
// { t: 'money', currency, amount } (amount in MINOR units, maybe fractional) |
// { t: 'lookup', v: id } |
// { t: 'list', items } | { t: 'empty' }.

const EMPTY = Object.freeze({ t: 'empty' });
const num = v => ({ t: 'number', v: snap(v) });
// A text VALUE: a literal, or what an element stores. Text a function or `&`
// builds goes through builtText() instead, which holds it to MAX_TEXT_LENGTH.
const txt = v => ({ t: 'text', v: String(v) });
const bool = v => ({ t: 'bool', v: !!v });
const isEmptyValue = v => !v || v.t === 'empty';

// ------------------------------------------------------------ the text cap

/** 10000 -> "10,000", without Intl (this file runs in Node and the browser alike). */
function groupedDigits(n) {
	return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * The one answer for text that would pass MAX_TEXT_LENGTH: a warning that
 * names where, a flag that stops the evaluation (evaluate() reads it), and a
 * blank. The warning is recorded even past the 20-warning budget — it is the
 * reason the whole formula is blank, and there is only ever one, because
 * nothing is evaluated after it.
 */
function textTooLong(rt, where) {
	rt.overflow = true;
	rt.warn(`${where}: the text would be longer than ${groupedDigits(MAX_TEXT_LENGTH)} characters`, true);
	return EMPTY;
}

/** The same answer for an evaluation past MAX_WORK. */
function overBudget(rt, where) {
	rt.overflow = true;
	rt.warn(`${where}: the formula would read more than ${groupedDigits(MAX_WORK)} characters or list entries`, true);
	return EMPTY;
}

/**
 * Charge `units` of work to this evaluation, before doing it. False once
 * past MAX_WORK (the formula is then blank). renderText's outside callers
 * carry no budget, and are never charged.
 */
function spend(rt, units, where) {
	if (!rt || typeof rt.work !== 'number') return true;
	if (rt.overflow) return false;
	rt.work += units;
	if (rt.work > MAX_WORK) { overBudget(rt, where); return false; }
	return true;
}

/**
 * The case-folded (lower-cased) form of a text, folded once per evaluation
 * and charged once: a classifier asking twenty contains() of one long body
 * folds it once. Null past the budget.
 */
function folded(s, rt, where) {
	const memo = rt && rt.folded;
	if (memo && memo.has(s)) return memo.get(s);
	if (!spend(rt, s.length, where)) return null;
	const out = s.toLowerCase();
	if (memo) memo.set(s, out);
	return out;
}

/** The text of a value a function is about to SCAN, charged; null past the budget. */
function scanned(value, rt, where) {
	const s = renderText(value, rt);
	return spend(rt, s.length, where) ? s : null;
}

/** Text a function built, held to the cap. */
function builtText(s, rt, where) {
	return s.length > MAX_TEXT_LENGTH ? textTooLong(rt, where) : txt(s);
}

/**
 * The texts of `values` joined, or the cap's blank — decided from the parts'
 * lengths BEFORE the joined string is made. A part is rendered only while
 * the running total still fits, so a join of fifty long notes stops at the
 * first one that does not.
 */
function joinWithin(values, rt, where) {
	const parts = [];
	let total = 0;
	for (let i = 0; i < values.length; i++) {
		const part = renderText(values[i], rt);
		total += part.length;
		if (total > MAX_TEXT_LENGTH) return textTooLong(rt, where);
		parts.push(part);
	}
	if (!spend(rt, total, where)) return EMPTY;
	return txt(parts.join(''));
}

/**
 * Whether replace()'s result will fit the cap, decided without building it:
 * the matches are counted the way split() finds them (left to right, never
 * overlapping), and the count stops as soon as the answer is known — a
 * growing replacement once it is past the cap, a shrinking one once enough
 * matches have been found. Only called where counting is needed (see
 * replace()).
 */
function replaceFits(text, search, replacement) {
	const grow = replacement.length - search.length;
	let count = 0;
	let at = text.indexOf(search);
	while (at !== -1) {
		count++;
		const length = text.length + count * grow;
		if (grow > 0 && length > MAX_TEXT_LENGTH) return false;
		if (grow < 0 && length <= MAX_TEXT_LENGTH) return true;
		at = text.indexOf(search, at + search.length);
	}
	return text.length + count * grow <= MAX_TEXT_LENGTH;
}

/** Kills float dust (0.1 + 0.2) without flattening tiny magnitudes. */
function snap(n) {
	if (!Number.isFinite(n)) return n;
	if (n === 0) return 0;
	return Number(n.toPrecision(15));
}
/** A magnitude converted out of a base: 12 significant digits, so a stored
 * base rounded upstream (68.26557 kg) still prints 150 lb 8 oz, not 7.997. */
function snapMagnitude(n) {
	if (!Number.isFinite(n)) return n;
	if (n === 0) return 0;
	return Number(n.toPrecision(12));
}

function measureOf(base, measure, unit) {
	return { t: 'measure', measure, base: snap(base), unit };
}

/** Magnitude of a measure value in its preferred unit (falls back to base). */
function magnitude(value, rt) {
	if (!value.unit || !rt.units) return value.base;
	try { return rt.units.fromBase(value.base, value.unit); } catch (e) { return value.base; }
}
/**
 * A money value. Integers stay exact (a stored amount may use all 16 digits
 * of MAX_SAFE_INTEGER, which snap()'s 15 significant digits would bend);
 * only a fractional mid-computation amount is snapped for float dust.
 */
function moneyOf(amount, currency) {
	const exact = Number.isInteger(amount) ? amount : snap(amount);
	return { t: 'money', currency, amount: exact === 0 ? 0 : exact };
}

/** The injected currency module, or a loud failure: money cannot be read, rounded or printed without it. */
function currencyModule(holder) {
	const currency = holder && holder.currency;
	if (!currency || typeof currency.exponentOf !== 'function') {
		throw new Error('A money formula needs the currency module — pass { currency } to analyze/evaluate/renderText.');
	}
	return currency;
}

/** 10 ^ exponent of a code: minor units per major unit (100 for USD, 1 for JPY). */
function minorPerMajor(code, rt) {
	const exponent = currencyModule(rt).exponentOf(code);
	return typeof exponent === 'number' ? 10 ** exponent : NaN;
}

/**
 * The one currency two money values share, or null. A BLANK seed (a blank
 * input read as zero, `blank: true`) takes the other side's currency: on an
 * element that allows USD and CAD the seed is a zero in the DEFAULT currency,
 * and an empty USD field must not make a CAD total a mismatch.
 */
function commonCurrency(l, r) {
	if (l.currency === r.currency) return l.currency;
	if (l.blank === true && l.amount === 0) return r.currency;
	if (r.blank === true && r.amount === 0) return l.currency;
	return null;
}
function currencyMismatch(l, r) {
	return `${l.currency} and ${r.currency} cannot be combined — there is no conversion between currencies`;
}

/** A zero standing in for a blank input (`blanks: 'zero'`): it takes the other side's measure (KPR-1085). */
function isBlankSeed(v) {
	return !!v && v.blank === true && v.base === 0;
}
function measureMismatch(l, r, rt) {
	// An own unit (KPR-1109) is named by its plural: "smidgens and a volume".
	const word = v => (isOwnUnitId(v.measure) ? measureWord(v.measure, rt) : `a ${v.measure}`);
	return `${word(l)} and ${word(r)} cannot be combined — there is no conversion between measures`;
}
/** Two measure values in DIFFERENT measures, neither a blank seed (KPR-1085). */
function measuresDiffer(l, r) {
	return !!l.measure && !!r.measure && l.measure !== r.measure && !isBlankSeed(l) && !isBlankSeed(r);
}

function fromMagnitude(m, like, rt) {
	if (!like.unit || !rt.units) return measureOf(m, like.measure, like.unit);
	try { return measureOf(rt.units.toBase(m, like.unit), like.measure, like.unit); } catch (e) { return measureOf(m, like.measure, like.unit); }
}

// Date plumbing: dates are 'YYYY-MM-DD', datetimes ISO strings, all UTC.
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
function parseDate(v) {
	const m = DATE_RE.exec(String(v));
	if (!m) return NaN;
	const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
	const d = new Date(ms);
	return d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]) ? ms : NaN;
}
// Strict ISO-8601 only, and UTC when no offset is given: Date.parse would
// read an offset-less timestamp in the HOST's zone, so the web preview and
// the API's stored value would disagree.
const DATETIME_RE = /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(Z|[+-]\d{2}:?\d{2})?)?$/;
const MAX_DATE_MS = 8.64e15;
function parseDateTime(v) {
	if (typeof v !== 'string' && !(v instanceof Date)) return NaN;
	const s = v instanceof Date ? v.toISOString() : v.trim();
	const m = DATETIME_RE.exec(s);
	if (!m) return NaN;
	if (m[2] === undefined) return parseDate(m[1]);
	const iso = `${m[1]}T${m[2]}:${m[3]}:${m[4] || '00'}.${(m[5] || '0').padEnd(3, '0')}${m[6] ? m[6].replace(/^([+-]\d{2})(\d{2})$/, '$1:$2') : 'Z'}`;
	const ms = Date.parse(iso);
	return Number.isNaN(ms) ? NaN : ms;
}
function msOf(value) {
	return value.t === 'date' ? parseDate(value.v) : parseDateTime(value.v);
}
function isoDate(ms) { return new Date(ms).toISOString().slice(0, 10); }
function isoDateTime(ms) { return new Date(ms).toISOString(); }
function dateValue(ms, kind) {
	if (!Number.isFinite(ms) || Math.abs(ms) > MAX_DATE_MS) return EMPTY;
	return kind === 'date' ? { t: 'date', v: isoDate(ms) } : { t: 'datetime', v: isoDateTime(ms) };
}
function addMonths(ms, months) {
	const d = new Date(ms);
	const y = d.getUTCFullYear();
	const m = d.getUTCMonth() + months;
	const day = d.getUTCDate();
	const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
	return Date.UTC(y, m, Math.min(day, last), d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds(), d.getUTCMilliseconds());
}
function addUnit(ms, n, unit) {
	switch (unit) {
		case 'd': return ms + n * DAY_MS;
		case 'w': return ms + n * 7 * DAY_MS;
		case 'm': return addMonths(ms, n);
		case 'y': return addMonths(ms, n * 12);
		default: return NaN;
	}
}
// --------------------------------------------------------------- the clock
//
// A `time` is a wall clock, stored exactly as utils/dateOptions.js coerceTime
// writes it: zero-padded 'HH:mm', with ':ss' only when the seconds are not
// zero. Every clock value in this module round-trips through secondsOfDay()
// and timeValue(), so nothing here can ever mint '24:00' — the wrap lands on
// '00:00'.
const DAY_S = 86400;
// Deliberately the SAME shape utils/dateOptions.js CLOCK accepts (1- or
// 2-digit hours in, always 2 out). An ISO timestamp is NOT a clock: the row
// records no zone, so reading a wall clock out of an instant would be a
// guess. dateOptions.comparable() refuses one for that reason and so does
// parseClock — the two must agree or a bound and a formula would disagree
// about the same stored value.
const CLOCK_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
function parseClock(raw) {
	if (typeof raw !== 'string') return null;
	const m = CLOCK_RE.exec(raw.trim());
	if (!m) return null;
	const hh = Number(m[1]);
	const mm = Number(m[2]);
	const ss = m[3] === undefined ? 0 : Number(m[3]);
	if (hh > 23 || mm > 59 || ss > 59) return null;
	return timeValue(hh * 3600 + mm * 60 + ss);
}
/** Seconds since midnight of a tagged time value. */
function secondsOfDay(value) {
	const m = CLOCK_RE.exec(String(value.v));
	if (!m) return NaN;
	return Number(m[1]) * 3600 + Number(m[2]) * 60 + (m[3] === undefined ? 0 : Number(m[3]));
}
/** Seconds since midnight -> a tagged time, WRAPPED into [00:00, 24:00). */
function timeValue(seconds) {
	if (!Number.isFinite(seconds)) return EMPTY;
	let s = Math.round(seconds) % DAY_S;
	if (s < 0) s += DAY_S;
	const pad = n => (n < 10 ? '0' + n : String(n));
	const hh = Math.floor(s / 3600);
	const mm = Math.floor((s % 3600) / 60);
	const ss = s % 60;
	return { t: 'time', v: ss ? pad(hh) + ':' + pad(mm) + ':' + pad(ss) : pad(hh) + ':' + pad(mm) };
}
/** A number of seconds as a `duration` measurement, displayed as "8 h 30 min". */
function durationOf(seconds) {
	if (!Number.isFinite(seconds)) return EMPTY;
	return measureOf(seconds, DURATION, 'h-min');
}
/** Seconds carried by a duration measurement (its base IS seconds). */
function durationSeconds(value) {
	return typeof value.base === 'number' ? value.base : NaN;
}
/** The UTC wall clock of a tagged datetime. */
function clockOfDateTime(value) {
	const ms = msOf(value);
	if (!Number.isFinite(ms)) return EMPTY;
	const d = new Date(ms);
	return timeValue(d.getUTCHours() * 3600 + d.getUTCMinutes() * 60 + d.getUTCSeconds());
}

function wholeMonthsBetween(a, b) { // a - b in whole calendar months (sign-aware)
	const da = new Date(a);
	const db = new Date(b);
	let months = (da.getUTCFullYear() - db.getUTCFullYear()) * 12 + (da.getUTCMonth() - db.getUTCMonth());
	if (a >= b) {
		if (da.getUTCDate() < db.getUTCDate()) months -= 1;
	} else if (da.getUTCDate() > db.getUTCDate()) {
		months += 1;
	}
	return months;
}

/** The fewest decimals (<= 6) that print `n` exactly. */
function decimalsOf(n) {
	for (let d = 0; d < 6; d++) {
		if (Math.abs(Math.round(n * 10 ** d) / 10 ** d - n) < 1e-9) return d;
	}
	return 6;
}

/** Integer minor units, rounded half away from zero; null past MAX_SAFE_INTEGER. */
function minorAmountOf(value) {
	// An amount that is already whole is returned untouched: roundTo nudges
	// by an epsilon scaled to the magnitude, which is a whole unit or more
	// past 2^52 and would move a large stored amount (or push the largest
	// one out of the safe range) on an unchanged copy.
	const rounded = Number.isInteger(value.amount) ? value.amount : roundTo(value.amount, 0);
	if (!Number.isSafeInteger(rounded)) return null;
	return rounded === 0 ? 0 : rounded;
}

/**
 * Text rendering of any scalar, the way the platform would print it.
 * `rt` is `{ units, currency }` — the same injections evaluate() takes.
 */
function renderText(value, rt) {
	switch (value.t) {
		case 'empty': return '';
		case 'text': return value.v;
		case 'number': return String(value.v);
		// renderText is always TEXT (string concatenation, `text()`), so a
		// flag spells itself here even though toStored materialises it as a
		// real boolean for a `boolean` element.
		case 'bool': return value.v ? 'true' : 'false';
		// A clock renders as the clock: without this case a `time` would fall
		// to `default: return ''` and every `&` concat would silently empty.
		case 'date': case 'datetime': case 'time': case 'lookup': return String(value.v);
		case 'measure': {
			const units = rt.units;
			const unit = value.unit || (units && units.getMeasure && units.getMeasure(value.measure) && units.getMeasure(value.measure).canonical) || null;
			// An own unit (KPR-1109): its number and its name, singular or plural
			// as the formatter chooses ("3 smidgens"), from the names the
			// evaluation read it with.
			if (isOwnUnitId(unit)) {
				const named = (rt.ownUnits && rt.ownUnits[unit]) || { id: unit, singular: measureWord(unit), plural: measureWord(unit) };
				if (units && typeof units.formatMeasurement === 'function') {
					try {
						const stored = { value: snapMagnitude(value.base), unit, base: value.base };
						return units.formatMeasurement(stored, { measure: unit, defaultUnit: unit, ownUnits: [named], display: { unit: 'entered', precision: decimalsOf(stored.value) } });
					} catch (e) { /* fall through */ }
				}
				return `${snapMagnitude(value.base)} ${named.plural}`;
			}
			if (units && unit && typeof units.formatMeasurement === 'function') {
				try {
					const stored = { value: snapMagnitude(units.fromBase(value.base, unit)), unit, base: value.base };
					// In the unit it arrived in, at the fewest decimals that print
					// it exactly (150 lb, 8.4375 lb) — the template renderer's rule.
					// A composite (lb-oz) keeps the formatter's own minor-part
					// rounding: its magnitude is decimal pounds, whose "exact"
					// decimals are float dust from the base conversion.
					const composite = typeof units.isComposite === 'function' && units.isComposite(unit);
					const display = composite ? { unit: 'entered' } : { unit: 'entered', precision: decimalsOf(stored.value) };
					return units.formatMeasurement(stored, { measure: value.measure, defaultUnit: unit, display });
				} catch (e) { /* fall through */ }
			}
			return unit ? `${snapMagnitude(magnitude(value, rt))} ${unit}` : String(value.base);
		}
		// Through the shared formatter, rounded to the currency's decimals
		// (the text a $4.1666… share prints is $4.17). `options` rides only on
		// a value read straight from an element, so `{{price}} & ""` on a
		// CAD-only element prints "$12.00" as the element does; anything
		// computed prints the unambiguous symbol ("CA$12.00") — the no-context
		// rule, and the one the API's text coercion applies to a stored result.
		case 'money': {
			const amount = minorAmountOf(value);
			return amount === null ? '' : currencyModule(rt).formatAmount(amount, value.currency, value.options || null);
		}
		case 'list': return value.items.map(item => renderText(item, rt)).join(', ');
		default: return '';
	}
}

const argTypes = (spec) => spec;

// ------------------------------------------------------ typed arguments
//
// KPR-349. `args` above is the free-text label an editor prints. `params` is
// the same list described BY TYPE, so an editor can tell that the second
// argument of convert() is a unit and offer the units instead of a blank box:
//
//   { name, kind, accepts, optional?, repeat?, choices?, measureOf? }
//
// kind     the INPUT the editor should offer — 'element' (an element or any
//          expression), 'number', 'text', 'unit' (a unit, written as a quoted
//          text) or 'choice' (one of `choices`, written as a quoted text).
// accepts  what the analyser accepts there, in its own type words — number,
//          text, bool, date, datetime, time, measure, money, list, group — or
//          ['any'] for any single value. It describes the check()s below; it
//          never replaces them, and nothing here is read by analyze() or
//          evaluate(): the grammar, the storage and the arithmetic are as
//          they were.
// repeat   the last param may be given any number of times (the '…' label).
// choices  [{ value, label, group }]; a unit choice also carries `measure`.
// measureOf  the unit must belong to the measure of the argument at that index.
//
// A unit param's choices come from the units registry, which this file does
// not require (the caller injects it, as it does for analyze/evaluate): they
// appear only when describeFunctions() is handed `{ units }`.
const ANY_VALUE = ['any'];
const DATEISH = ['date', 'datetime'];
const CLOCKISH = ['time', 'datetime'];
const DATE_OR_CLOCK = ['date', 'datetime', 'time'];
const param = (kind, name, accepts, more) => Object.assign({ name, kind, accepts }, more);
const P = {
	element: (name, accepts, more) => param('element', name, accepts, more),
	number: (name, accepts, more) => param('number', name, accepts, more),
	text: (name, more) => param('text', name, ANY_VALUE, more),
	unit: (name, more) => param('unit', name, ['text'], more),
	choice: (name, choices, more) => param('choice', name, ['text'], Object.assign({ choices }, more)),
};

function expectNumberLike(t, ctx, what) {
	if (t.type === 'number' || t.type === 'measure' || t.type === 'money' || t.type === 'empty') return t;
	return ctx.fail(`${what} needs a number or a measurement, not ${typeLabel(t)}.`);
}
function expectText(t, ctx, what) {
	if (t.type === 'list') return ctx.fail(`${what} needs a single value, not a ${typeLabel(t)}.`);
	if (t.type === 'entries') return ctx.fail(entriesMisused(what, t));
	return T.text();
}

/**
 * The sentence for a group's entries where a value is wanted (KPR-686): what
 * the author can write instead, with the group's own name.
 */
function entriesMisused(what, t) {
	const g = t && t.group ? t.group : 'group';
	return `${what} cannot read a repeating group's entries — read a column with {{${g}.<column>}}, count the entries with count({{${g}}}), or test them with any() or all().`;
}
function expectDate(t, ctx, what) {
	if (isDateish(t) || t.type === 'empty') return t;
	return ctx.fail(isClockish(t)
		? `${what} needs a date, not a time — a wall clock has no calendar.`
		: `${what} needs a date, not ${typeLabel(t)}.`);
}
function expectTime(t, ctx, what) {
	if (isClockish(t) || t.type === 'empty') return t;
	return ctx.fail(`${what} needs a time, not ${typeLabel(t)}.`);
}
// Clock units are the units registry's OWN ids (min, h, s), so the spelling a
// user types in a measurement field is the spelling dateDiff takes. "m" is
// NOT among them and never will be: it already means MONTHS here, and `5 m`
// tokenizes as five METRES.
const CALENDAR_UNITS = ['d', 'w', 'm', 'y'];
const CLOCK_UNITS = ['h', 'min', 's'];
const CLOCK_UNIT_SECONDS = { h: 3600, min: 60, s: 1 };
const CLOCK_UNIT_ERROR = 'dateDiff() on times takes "h", "min" or "s" — "m" means months.';
// The words dateDiff() and dateAdd() take, built from the two lists the
// functions themselves check against so the picker and the check cannot part.
// "m" is MONTHS and "min" is minutes — see CALENDAR_UNITS above.
const DATE_PART_LABELS = { d: 'Days', w: 'Weeks', m: 'Months', y: 'Years', h: 'Hours', min: 'Minutes', s: 'Seconds' };
const DATE_PART_CHOICES = [
	...CALENDAR_UNITS.map(value => ({ value, label: DATE_PART_LABELS[value], group: 'Calendar' })),
	...CLOCK_UNITS.map(value => ({ value, label: DATE_PART_LABELS[value], group: 'Clock' })),
];

// hour()/minute()/second() read a CLOCK. A plain `date` has none — reading
// one would answer 0 for every record and look like data.
function expectClockPart(t, ctx, what) {
	if (isClockish(t) || t.type === 'datetime' || t.type === 'empty') return t;
	return ctx.fail(`${what} needs a time or a date-time, not ${typeLabel(t)}.`);
}
function clockPart(value, divisor, modulo) {
	if (isEmptyValue(value)) return EMPTY;
	const clock = value.t === 'time' ? value : (value.t === 'datetime' ? clockOfDateTime(value) : EMPTY);
	if (isEmptyValue(clock)) return EMPTY;
	const seconds = secondsOfDay(clock);
	return Number.isFinite(seconds) ? num(Math.floor(seconds / divisor) % modulo) : EMPTY;
}
function expectNumber(t, ctx, what) {
	if (t.type === 'number' || t.type === 'empty') return T.number();
	return ctx.fail(`${what} needs a number, not ${typeLabel(t)}.`);
}

// A blank input read as zero (blanks: 'zero') carries `blank: true`: the
// arithmetic operators use the zero, while isEmpty()/coalesce()/count() and
// the aggregates still see a blank — "skips blanks" must mean the same in
// both modes, or avg() over three inputs with one blank would divide by 3.
const isBlankInput = v => !v || v.t === 'empty' || v.blank === true || (v.t === 'text' && v.v === '');

/** Flattens lists and drops blanks for the variadic aggregates; every list entry is charged. */
function spread(values, rt, where) {
	let entries = 0;
	values.forEach(v => { if (v && v.t === 'list') entries += v.items.length; });
	if (entries && !spend(rt, entries * LIST_ENTRY_COST, where)) return [];
	const out = [];
	values.forEach(v => {
		if (v.t === 'list') v.items.forEach(item => { if (!isBlankInput(item)) out.push(item); });
		else if (!isBlankInput(v)) out.push(v);
	});
	return out;
}

/**
 * any()/all()'s ONE-argument shape (KPR-686): a list of yes/no. Its
 * two-argument shape — a group's entries and a condition — is the analyser's
 * own (visitCall), because the condition is read in an entry's scope.
 */
function checkYesNoList(a, ctx, name) {
	if (a.length === 2) return ctx.fail(`${name} with a condition takes a repeating group first: ${name.slice(0, -2)}({{group}}, {{column}} > 10).`);
	if (a.length !== 1) ctx.fail(`${name} takes a list of yes/no values, or a repeating group and a condition.`);
	const t = a[0];
	if (t.type === 'entries') ctx.fail(`${name} over a group's entries needs a condition for each one: ${name.slice(0, -2)}({{${t.group || 'group'}}}, {{column}} > 10).`);
	if (t.type === 'empty') return T.bool();
	if (t.type !== 'list' || (t.item.type !== 'bool' && t.item.type !== 'empty')) {
		ctx.fail(`${name} needs a list of yes/no values (a yes/no column of a group: {{group.column}}), not ${typeLabel(t)}.`);
	}
	return T.bool();
}

/** any()/all() over a list of yes/no: `wantAny` true for any(). A blank is not a yes. */
function yesNoList(list, rt, where, wantAny) {
	const items = list && list.t === 'list' ? list.items : [];
	if (items.length && !spend(rt, items.length * LIST_ENTRY_COST, where)) return EMPTY;
	for (let i = 0; i < items.length; i++) {
		const yes = !!items[i] && items[i].t === 'bool' && items[i].v === true;
		if (wantAny && yes) return bool(true);
		if (!wantAny && !yes) return bool(false);
	}
	return bool(!wantAny);
}

/** A typed zero for sum() over nothing: the first argument says the shape. */
function zeroLike(values, rt) {
	for (const v of values) {
		const t = v && v.t === 'list' ? v.itemType : null;
		if (v && v.t === 'measure') return measureOf(0, v.measure, v.unit);
		if (t && t.type === 'measure') {
			const unit = rt.defaultUnitOf ? rt.defaultUnitOf(t) : null;
			// The default unit's measure when the type allows several (KPR-1085).
			const own = t.measure || (unit && rt.units && rt.units.getUnit(unit) ? rt.units.getUnit(unit).measure : null);
			return measureOf(0, own, unit);
		}
		if (v && v.t === 'money') return moneyOf(0, v.currency);
		if (t && t.type === 'money') {
			const code = rt.defaultCurrencyOf ? rt.defaultCurrencyOf(t) : null;
			return code ? moneyOf(0, code) : EMPTY;
		}
		if (v && v.t === 'number') return num(0);
		if (t && t.type === 'number') return num(0);
	}
	return num(0);
}

/** The common numeric type of variadic args (numbers, measures of one measure, or money of one currency). */
function aggregateType(types, ctx, name) {
	let result = T.empty();
	types.forEach(t => {
		const item = t.type === 'list' ? t.item : t;
		if (item.type === 'empty') return;
		if (item.type !== 'number' && item.type !== 'measure' && item.type !== 'money') {
			ctx.fail(`${name}() works on numbers or measurements, not ${typeLabel(item)}.`);
		}
		if (result.type === 'empty') {
			if (item.type === 'measure') result = T.measure(item.measure, item.measures);
			else if (item.type === 'money') result = T.money(item.currencies);
			else result = T.number();
		} else if (!sameType(result, item)) ctx.fail(`${name}() cannot mix ${typeLabel(result)} and ${typeLabel(item)}.`);
		// Every argument must land in ONE currency, so the result is the
		// codes all of them may hold.
		else if (result.type === 'money') result = T.money(currencyMeet(result, item));
		// …and in one measure (KPR-1085): the measures all of them may be in.
		else if (result.type === 'measure' && (result.measures || item.measures)) {
			const meet = measureMeet(result, item);
			result = meet ? T.measure(null, meet) : T.measure(null);
		}
	});
	return result;
}

/**
 * All numbers -> plain numbers; all measures -> bases; all money -> minor
 * amounts. Mixed kinds never get here (the analyser refused them), but two
 * CURRENCIES can — a USD-or-CAD element is one static type — so money
 * answers null for a mixed set and the caller warns and blanks.
 */
function toNumeric(values, rt) {
	if (!values.length) return { kind: 'number', xs: [] };
	if (values[0].t === 'measure') {
		// One measure (KPR-1085): values of an element that allows several may
		// be a mass and a volume, which have no sum, order or average -- the
		// caller blanks, money's rule. A blank seed takes any measure.
		let like = null;
		for (const v of values) {
			if (isBlankSeed(v)) continue;
			if (like === null) like = v;
			else if (v.measure && like.measure && v.measure !== like.measure) {
				rt.warn(measureMismatch(like, v, rt));
				return null;
			}
		}
		return { kind: 'measure', like: like && like.measure !== values[0].measure ? like : values[0], xs: values.map(v => v.base) };
	}
	if (values[0].t === 'money') {
		let code = null;
		for (const v of values) {
			// A blank seed (amount 0, `blank`) never decides the currency.
			if (v.blank === true && v.amount === 0) continue;
			if (code === null) code = v.currency;
			else if (v.currency !== code) {
				rt.warn(currencyMismatch({ currency: code }, v));
				return null;
			}
		}
		return { kind: 'money', code: code || values[0].currency, xs: values.map(v => v.amount) };
	}
	return { kind: 'number', xs: values.map(v => v.v) };
}
function fromNumeric(x, group) {
	if (group.kind === 'measure') return measureOf(x, group.like.measure, group.like.unit);
	if (group.kind === 'money') return moneyOf(x, group.code);
	return num(x);
}

/**
 * Money rounded in MAJOR units: `round({{price}}, 0)` is whole dollars and
 * `floor({{price}})` drops the cents, exactly as they would on the number a
 * person reads. `step(major)` rounds a major-unit number; the scale comes
 * from the currency's exponent (JPY has none, so floor() of yen is itself).
 */
function roundMoney(x, rt, step) {
	const scale = minorPerMajor(x.currency, rt);
	if (!Number.isFinite(scale)) { rt.warn(`"${x.currency}" is not a currency`); return EMPTY; }
	// snap() first: 1900.0000000001 / 100 must ceil to 19, not 20.
	return moneyOf(step(snap(x.amount / scale)) * scale, x.currency);
}

/** Half away from zero (the platform's roundHalfAway), to `decimals` places (0–12). */
function roundTo(x, decimals) {
	const f = 10 ** Math.max(0, Math.min(12, Math.trunc(decimals)));
	const scaled = Math.abs(x) * f;
	const rounded = Math.round(scaled + Number.EPSILON * scaled);
	return Math.sign(x) * rounded / f;
}

const FUNCTIONS = {
	// ---------------------------------------------------------------- math
	round: {
		group: 'Math', args: argTypes(['number', 'decimals?']), description: 'Rounds to a number of decimals (default 0).', example: 'round({{price}} * 1.2, 2)',
		params: [P.number('number', ['number', 'measure', 'money']), P.number('decimals', ['number'], { optional: true })],
		check: (a, ctx) => {
			if (a.length < 1 || a.length > 2) ctx.fail('round() takes a value and an optional number of decimals.');
			if (a[1]) expectNumber(a[1], ctx, 'round()');
			return expectNumberLike(a[0], ctx, 'round()');
		},
		apply: ([x, d], rt) => {
			if (isEmptyValue(x)) return EMPTY;
			const decimals = d && !isEmptyValue(d) ? d.v : 0;
			if (x.t === 'measure') return fromMagnitude(roundTo(magnitude(x, rt), decimals), x, rt);
			if (x.t === 'money') return roundMoney(x, rt, major => roundTo(major, decimals));
			return num(roundTo(x.v, decimals));
		},
	},
	floor: {
		group: 'Math', args: ['number'], description: 'Rounds down to a whole number.', example: 'floor({{hours}})',
		params: [P.number('number', ['number', 'measure', 'money'])],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('floor() takes one value.'); return expectNumberLike(a[0], ctx, 'floor()'); },
		apply: ([x], rt) => {
			if (isEmptyValue(x)) return EMPTY;
			if (x.t === 'money') return roundMoney(x, rt, Math.floor);
			return x.t === 'measure' ? fromMagnitude(Math.floor(magnitude(x, rt)), x, rt) : num(Math.floor(x.v));
		},
	},
	ceil: {
		group: 'Math', args: ['number'], description: 'Rounds up to a whole number.', example: 'ceil({{pages}} / 10)',
		params: [P.number('number', ['number', 'measure', 'money'])],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('ceil() takes one value.'); return expectNumberLike(a[0], ctx, 'ceil()'); },
		apply: ([x], rt) => {
			if (isEmptyValue(x)) return EMPTY;
			if (x.t === 'money') return roundMoney(x, rt, Math.ceil);
			return x.t === 'measure' ? fromMagnitude(Math.ceil(magnitude(x, rt)), x, rt) : num(Math.ceil(x.v));
		},
	},
	abs: {
		group: 'Math', args: ['number'], description: 'The value without its sign.', example: 'abs({{delta}})',
		params: [P.number('number', ['number', 'measure', 'money'])],
		check: (a, ctx) => {
			if (a.length !== 1) ctx.fail('abs() takes one value.');
			if (ctx.affineMeasureOf(a[0])) ctx.fail(`abs() has no meaning for ${ctx.affineName(ctx.affineMeasureOf(a[0])).one}.`);
			return expectNumberLike(a[0], ctx, 'abs()');
		},
		apply: ([x]) => {
			if (isEmptyValue(x)) return EMPTY;
			if (x.t === 'money') return moneyOf(Math.abs(x.amount), x.currency);
			return x.t === 'measure' ? measureOf(Math.abs(x.base), x.measure, x.unit) : num(Math.abs(x.v));
		},
	},
	sqrt: {
		group: 'Math', args: ['number'], description: 'Square root.', example: 'sqrt({{area}})',
		params: [P.number('number', ['number'])],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('sqrt() takes one number.'); return expectNumber(a[0], ctx, 'sqrt()'); },
		apply: ([x], rt) => {
			if (isEmptyValue(x)) return EMPTY;
			if (x.v < 0) { rt.warn('sqrt() of a negative number'); return EMPTY; }
			return num(Math.sqrt(x.v));
		},
	},
	min: {
		group: 'Math', args: ['value', '…'], description: 'The smallest of the values (lists are spread) — or, given a group\'s column and a condition, of the entries that meet it.', example: 'min({{a}}, {{b}}, 100)',
		params: [P.element('value', ['number', 'measure', 'money', 'list'], { repeat: true })],
		check: (a, ctx) => { if (!a.length) ctx.fail('min() needs at least one value.'); return aggregateType(a, ctx, 'min'); },
		apply: (values, rt) => { const xs = spread(values, rt, 'min()'); if (!xs.length) return EMPTY; const g = toNumeric(xs, rt); if (!g) return EMPTY; return fromNumeric(g.xs.reduce((m, x) => (x < m ? x : m), g.xs[0]), g); },
	},
	max: {
		group: 'Math', args: ['value', '…'], description: 'The largest of the values (lists are spread) — or, given a group\'s column and a condition, of the entries that meet it.', example: 'max({{a}}, {{b}})',
		params: [P.element('value', ['number', 'measure', 'money', 'list'], { repeat: true })],
		check: (a, ctx) => { if (!a.length) ctx.fail('max() needs at least one value.'); return aggregateType(a, ctx, 'max'); },
		apply: (values, rt) => { const xs = spread(values, rt, 'max()'); if (!xs.length) return EMPTY; const g = toNumeric(xs, rt); if (!g) return EMPTY; return fromNumeric(g.xs.reduce((m, x) => (x > m ? x : m), g.xs[0]), g); },
	},
	sum: {
		group: 'Math', args: ['value', '…'], description: 'Adds the values (lists are spread; blanks are skipped) — or, given a group\'s column and a condition, only the entries that meet it.', example: 'sum({{parts.weight}})',
		params: [P.element('value', ['number', 'measure', 'money', 'list'], { repeat: true })],
		check: (a, ctx) => {
			if (!a.length) ctx.fail('sum() needs at least one value.');
			const t = aggregateType(a, ctx, 'sum');
			if (ctx.affineMeasureOf(t)) ctx.fail(`${ctx.affineName(ctx.affineMeasureOf(t)).plural} cannot be summed — avg(), min() and max() work.`);
			return t;
		},
		apply: (values, rt) => {
			const xs = spread(values, rt, 'sum()');
			// A total over a group's column is 0 when nothing is filled in, in
			// either blanks mode (KPR-686, the header): `blanks` is about the
			// item's own single values. A lookup walk's list keeps its rule.
			if (!xs.length) return (rt.blanks === 'empty' && !values.some(v => v && v.fromEntries)) || rt.overflow ? EMPTY : zeroLike(values, rt);
			const g = toNumeric(xs, rt);
			if (!g) return EMPTY;
			return fromNumeric(g.xs.reduce((s, x) => s + x, 0), g);
		},
	},
	avg: {
		group: 'Math', args: ['value', '…'], description: 'The average of the values (lists are spread; blanks are skipped) — or, given a group\'s column and a condition, of the entries that meet it.', example: 'avg({{q1}}, {{q2}}, {{q3}})',
		params: [P.element('value', ['number', 'measure', 'money', 'list'], { repeat: true })],
		check: (a, ctx) => { if (!a.length) ctx.fail('avg() needs at least one value.'); return aggregateType(a, ctx, 'avg'); },
		apply: (values, rt) => { const xs = spread(values, rt, 'avg()'); if (!xs.length) return EMPTY; const g = toNumeric(xs, rt); if (!g) return EMPTY; return fromNumeric(g.xs.reduce((s, x) => s + x, 0) / g.xs.length, g); },
	},
	count: {
		group: 'Math', args: ['value', '…'], description: 'How many of the values are filled in (lists are spread; a repeating group counts its entries) — or, given a group and a condition, how many entries meet it.', example: 'count({{parts}})',
		params: [P.element('value', ANY_VALUE, { repeat: true })],
		check: (a, ctx) => { if (!a.length) ctx.fail('count() needs at least one value.'); return T.number(); },
		// A group's entries count one each, a heading never (KPR-686).
		apply: (values, rt) => {
			let entries = 0;
			const rest = [];
			values.forEach(v => { if (v && v.t === 'entries') entries += v.items.length; else rest.push(v); });
			return num(entries + spread(rest, rt, 'count()').length);
		},
	},

	// --------------------------------------------------------------- logic
	if: {
		group: 'Logic', args: ['condition', 'then', 'else'], description: 'One value when the condition holds, the other otherwise.', example: 'if({{score}} >= 90, "A", "B")',
		params: [P.element('condition', ['bool']), P.element('then', ANY_VALUE), P.element('else', ANY_VALUE)],
		check: (a, ctx) => {
			if (a.length !== 3) ctx.fail('if() takes a condition, a value when true, and a value when false.');
			if (a[0].type !== 'bool' && a[0].type !== 'empty') ctx.fail(`if() needs a true/false condition, not ${typeLabel(a[0])}.`);
			return unify(a[1], a[2], ctx.pos, 'if()');
		},
		apply: ([c, a, b]) => (!isEmptyValue(c) && c.v ? a : b),
		lazy: true,
	},
	isempty: {
		group: 'Logic', args: ['value'], description: 'True when the value is blank.', example: 'if(isEmpty({{notes}}), "none", {{notes}})',
		params: [P.element('value', ANY_VALUE)],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('isEmpty() takes one value.'); return T.bool(); },
		apply: ([x], rt) => bool((x && x.t === 'entries') ? !x.items.length : (isBlankInput(x) || (x.t === 'list' && !spread([x], rt, 'isEmpty()').length))),
	},
	coalesce: {
		group: 'Logic', args: ['value', '…'], description: 'The first value that is not blank.', example: 'coalesce({{nickname}}, {{name}})',
		params: [P.element('value', ANY_VALUE, { repeat: true })],
		check: (a, ctx) => {
			if (!a.length) ctx.fail('coalesce() needs at least one value.');
			return a.reduce((acc, t) => unify(acc, t, ctx.pos, 'coalesce()'), T.empty());
		},
		apply: (values) => values.find(v => !isBlankInput(v)) || EMPTY,
	},
	blank: {
		group: 'Logic', args: [], description: 'A blank value (leave the element empty).', example: 'if({{qty}} > 0, {{qty}} * {{price}}, blank())',
		params: [],
		check: (a, ctx) => { if (a.length) ctx.fail('blank() takes no values.'); return T.empty(); },
		apply: () => EMPTY,
	},
	// ANY / ALL (KPR-686). Two shapes each: a list of yes/no values (a yes/no
	// column of a group, a multi-lookup walk's yes/no), or a group's entries
	// and a condition read once per entry, in that entry's scope (the header).
	// A blank is not a yes. Nothing to test: any() is false and all() is true.
	// The two-argument shape is the analyser's and the evaluator's own
	// (`scoped`): its condition must be read in the entry's scope, which no
	// check() over already-typed arguments can do.
	any: {
		group: 'Logic', args: ['yes/no list or {{group}}', 'condition?'], description: 'True when any value is yes — or, given a repeating group and a condition, when any entry meets it.', example: 'any({{lines}}, {{quantity}} > 10)',
		params: [P.element('list', ['list', 'group']), P.element('condition', ['bool'], { optional: true })],
		scoped: true,
		check: (a, ctx) => checkYesNoList(a, ctx, 'any()'),
		apply: ([list], rt) => yesNoList(list, rt, 'any()', true),
	},
	all: {
		group: 'Logic', args: ['yes/no list or {{group}}', 'condition?'], description: 'True when every value is yes — or, given a repeating group and a condition, when every entry meets it (true when there is none).', example: 'all({{steps}}, {{done}})',
		params: [P.element('list', ['list', 'group']), P.element('condition', ['bool'], { optional: true })],
		scoped: true,
		check: (a, ctx) => checkYesNoList(a, ctx, 'all()'),
		apply: ([list], rt) => yesNoList(list, rt, 'all()', false),
	},

	// ---------------------------------------------------------------- text
	concat: {
		group: 'Text', args: ['value', '…'], description: 'Joins values into one text (same as &).', example: 'concat({{first}}, " ", {{last}})',
		params: [P.element('value', ANY_VALUE, { repeat: true, optional: true })],
		check: (a, ctx) => { a.forEach(t => expectText(t, ctx, 'concat()')); return T.text(); },
		apply: (values, rt) => joinWithin(values, rt, 'concat()'),
	},
	// Case mapping never shortens a text and can lengthen one ("ß" -> "SS"),
	// so an input already past the cap is refused before it is mapped, and
	// the mapped text is held to the cap after.
	upper: {
		group: 'Text', args: ['text'], description: 'UPPER CASE.', example: 'upper({{code}})',
		params: [P.text('text')],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('upper() takes one value.'); return expectText(a[0], ctx, 'upper()'); },
		apply: ([x], rt) => {
			if (isEmptyValue(x)) return EMPTY;
			const s = renderText(x, rt);
			if (s.length > MAX_TEXT_LENGTH) return textTooLong(rt, 'upper()');
			return spend(rt, s.length, 'upper()') ? builtText(s.toUpperCase(), rt, 'upper()') : EMPTY;
		},
	},
	lower: {
		group: 'Text', args: ['text'], description: 'lower case.', example: 'lower({{email}})',
		params: [P.text('text')],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('lower() takes one value.'); return expectText(a[0], ctx, 'lower()'); },
		apply: ([x], rt) => {
			if (isEmptyValue(x)) return EMPTY;
			const s = renderText(x, rt);
			if (s.length > MAX_TEXT_LENGTH) return textTooLong(rt, 'lower()');
			return spend(rt, s.length, 'lower()') ? builtText(s.toLowerCase(), rt, 'lower()') : EMPTY;
		},
	},
	trim: {
		group: 'Text', args: ['text'], description: 'Removes surrounding spaces.', example: 'trim({{name}})',
		params: [P.text('text')],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('trim() takes one value.'); return expectText(a[0], ctx, 'trim()'); },
		apply: ([x], rt) => {
			if (isEmptyValue(x)) return EMPTY;
			const s = scanned(x, rt, 'trim()');
			return s === null ? EMPTY : builtText(s.trim(), rt, 'trim()');
		},
	},
	len: {
		group: 'Text', args: ['text'], description: 'Number of characters.', example: 'len({{title}})',
		params: [P.text('text')],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('len() takes one value.'); expectText(a[0], ctx, 'len()'); return T.number(); },
		apply: ([x], rt) => num(isEmptyValue(x) ? 0 : renderText(x, rt).length),
	},
	left: {
		group: 'Text', args: ['text', 'count'], description: 'The first N characters.', example: 'left({{sku}}, 3)',
		params: [P.text('text'), P.number('count', ['number'])],
		check: (a, ctx) => { if (a.length !== 2) ctx.fail('left() takes a text and a count.'); expectText(a[0], ctx, 'left()'); expectNumber(a[1], ctx, 'left()'); return T.text(); },
		apply: ([x, n], rt) => {
			if (isEmptyValue(x) || isEmptyValue(n)) return EMPTY;
			const s = renderText(x, rt);
			const k = Math.min(s.length, Math.max(0, Math.trunc(n.v)));
			if (k > MAX_TEXT_LENGTH) return textTooLong(rt, 'left()');
			return spend(rt, k, 'left()') ? txt(s.slice(0, k)) : EMPTY;
		},
	},
	right: {
		group: 'Text', args: ['text', 'count'], description: 'The last N characters.', example: 'right({{phone}}, 4)',
		params: [P.text('text'), P.number('count', ['number'])],
		check: (a, ctx) => { if (a.length !== 2) ctx.fail('right() takes a text and a count.'); expectText(a[0], ctx, 'right()'); expectNumber(a[1], ctx, 'right()'); return T.text(); },
		apply: ([x, n], rt) => {
			if (isEmptyValue(x) || isEmptyValue(n)) return EMPTY;
			const s = renderText(x, rt);
			const k = Math.min(s.length, Math.max(0, Math.trunc(n.v)));
			if (k > MAX_TEXT_LENGTH) return textTooLong(rt, 'right()');
			if (!spend(rt, k, 'right()')) return EMPTY;
			return txt(k === 0 ? '' : s.slice(-k));
		},
	},
	contains: {
		group: 'Text', args: ['text', 'search'], description: 'True when the text contains the search (case-insensitive).', example: 'contains({{tags-text}}, "urgent")',
		params: [P.text('text'), P.text('search')],
		check: (a, ctx) => { if (a.length !== 2) ctx.fail('contains() takes a text and a search text.'); expectText(a[0], ctx, 'contains()'); expectText(a[1], ctx, 'contains()'); return T.bool(); },
		apply: ([x, s], rt) => {
			if (isEmptyValue(x) || isEmptyValue(s)) return bool(false);
			const text = folded(renderText(x, rt), rt, 'contains()');
			const search = text === null ? null : folded(renderText(s, rt), rt, 'contains()');
			if (search === null) return EMPTY;
			// Each DISTINCT search is charged the scan it makes of the text; the
			// same search of the same text is answered from memory.
			const answers = rt.searched ? (rt.searched.get(text) || new Map()) : null;
			if (answers && answers.has(search)) return bool(answers.get(search));
			if (!spend(rt, text.length, 'contains()')) return EMPTY;
			const found = text.includes(search);
			if (answers) { answers.set(search, found); rt.searched.set(text, answers); }
			return bool(found);
		},
	},
	replace: {
		group: 'Text', args: ['text', 'search', 'replacement'], description: 'Replaces every occurrence of the search text.', example: 'replace({{phone}}, "-", "")',
		params: [P.text('text'), P.text('search'), P.text('replacement')],
		check: (a, ctx) => { if (a.length !== 3) ctx.fail('replace() takes a text, a search text and a replacement.'); a.forEach(t => expectText(t, ctx, 'replace()')); return T.text(); },
		// The one function whose output can dwarf its input — the reason
		// MAX_TEXT_LENGTH exists. Its length is worked out from the match
		// count first, so an over-long result is never built at all.
		apply: ([x, s, r], rt) => {
			if (isEmptyValue(x)) return EMPTY;
			const text = renderText(x, rt);
			const search = isEmptyValue(s) ? '' : scanned(s, rt, 'replace()');
			if (search === null) return EMPTY;
			if (!search) return builtText(text, rt, 'replace()');
			const replacement = isEmptyValue(r) ? '' : scanned(r, rt, 'replace()');
			if (replacement === null) return EMPTY;
			const grow = replacement.length - search.length;
			// No longer than the search: the result is no longer than the text.
			// A text within the cap is split at once, with no count first; one
			// over it cannot come back under with an equal-length replacement.
			if (grow === 0 && text.length > MAX_TEXT_LENGTH) return textTooLong(rt, 'replace()');
			if (grow > 0 || text.length > MAX_TEXT_LENGTH) {
				// Count only as far as the answer needs (replaceFits), charged first.
				if (!spend(rt, text.length, 'replace()')) return EMPTY;
				if (!replaceFits(text, search, replacement)) return textTooLong(rt, 'replace()');
			}
			// The split allocates a piece per match — dearer than a scan (a
			// million matches take ~20 ms), so it is charged double.
			if (!spend(rt, text.length * 2, 'replace()')) return EMPTY;
			return txt(text.split(search).join(replacement));
		},
	},
	text: {
		group: 'Text', args: ['value'], description: 'The value written as text.', example: 'text({{weight}}) & " net"',
		params: [P.element('value', ANY_VALUE)],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('text() takes one value.'); return expectText(a[0], ctx, 'text()'); },
		apply: ([x], rt) => (isEmptyValue(x) ? EMPTY : builtText(renderText(x, rt), rt, 'text()')),
	},
	number: {
		group: 'Text', args: ['text'], description: 'Reads a number out of text (blank when there is none).', example: 'number({{code}})',
		params: [P.text('text')],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('number() takes one value.'); expectText(a[0], ctx, 'number()'); return T.number(); },
		apply: ([x], rt) => {
			if (isEmptyValue(x)) return EMPTY;
			if (x.t === 'number') return x;
			// The amount in MAJOR units, not a scrape of "-$3.00" (whose sign
			// sits before the symbol, out of the regex's reach).
			if (x.t === 'money') {
				const scale = minorPerMajor(x.currency, rt);
				return Number.isFinite(scale) ? num(x.amount / scale) : EMPTY;
			}
			// Two passes: the commas out, then the first number found.
			const s = renderText(x, rt);
			if (!spend(rt, s.length * 2, 'number()')) return EMPTY;
			const m = s.replace(/,/g, '').match(/-?\d+(?:\.\d+)?|-?\.\d+/);
			return m ? num(Number(m[0])) : EMPTY;
		},
	},

	// ---------------------------------------------------------------- date
	today: {
		group: 'Date', args: [], description: "Today's date. The value refreshes daily and on every save.", example: 'days(today(), {{start}})',
		params: [],
		check: (a, ctx) => { if (a.length) ctx.fail('today() takes no values.'); return T.date(); },
		apply: (_, rt) => dateValue(rt.now.getTime(), 'date'),
	},
	now: {
		group: 'Date', args: [], description: 'The current date and time. Refreshes daily and on every save.', example: 'now()',
		params: [],
		check: (a, ctx) => { if (a.length) ctx.fail('now() takes no values.'); return T.datetime(); },
		apply: (_, rt) => dateValue(rt.now.getTime(), 'datetime'),
	},
	days: {
		group: 'Date', args: ['later', 'earlier'], description: 'Days from the second date to the first (negative when reversed).', example: 'days({{end}}, {{start}})',
		params: [P.element('later', DATEISH), P.element('earlier', DATEISH)],
		check: (a, ctx) => { if (a.length !== 2) ctx.fail('days() takes two dates.'); expectDate(a[0], ctx, 'days()'); expectDate(a[1], ctx, 'days()'); return T.number(); },
		apply: ([a, b]) => {
			if (isEmptyValue(a) || isEmptyValue(b)) return EMPTY;
			const x = msOf(a);
			const y = msOf(b);
			if (!Number.isFinite(x) || !Number.isFinite(y)) return EMPTY;
			const d = (x - y) / DAY_MS;
			return num(a.t === 'date' && b.t === 'date' ? Math.round(d) : d);
		},
	},
	datediff: {
		group: 'Date', args: ['later', 'earlier', 'unit'], description: 'Whole units between two dates: "d" days, "w" weeks, "m" months, "y" years — or between two times or two date-times: "h" hours, "min" minutes, "s" seconds.', example: 'dateDiff(today(), {{hired}}, "y")',
		params: [P.element('later', DATE_OR_CLOCK), P.element('earlier', DATE_OR_CLOCK), P.choice('unit', DATE_PART_CHOICES)],
		check: (a, ctx) => {
			if (a.length !== 3) ctx.fail('dateDiff() takes two dates (or two times) and a unit ("d", "w", "m", "y", "h", "min" or "s").');
			// Two TIMES are a separate row of the table: a clock has no
			// calendar, so only the clock units apply and the answer is
			// SIGNED — 22:00 to 06:00 is -16 h, not 8. elapsed() is the
			// function that wraps.
			const clock = isClockish(a[0]) || isClockish(a[1]);
			if (clock) { expectTime(a[0], ctx, 'dateDiff()'); expectTime(a[1], ctx, 'dateDiff()'); }
			else { expectDate(a[0], ctx, 'dateDiff()'); expectDate(a[1], ctx, 'dateDiff()'); }
			expectText(a[2], ctx, 'dateDiff()');
			// Only a LITERAL unit can be judged here. The clock rows are new,
			// so a static refusal cannot break a stored formula; the calendar
			// rows keep their runtime warn-and-blank, which is what every
			// formula written before today was validated against.
			const literal = ctx.literalText(2);
			if (clock && literal !== null && !CLOCK_UNITS.includes(literal.trim().toLowerCase())) ctx.fail(CLOCK_UNIT_ERROR);
			return T.number();
		},
		apply: ([a, b, u], rt) => {
			if (isEmptyValue(a) || isEmptyValue(b) || isEmptyValue(u)) return EMPTY;
			const unitText = scanned(u, rt, 'dateDiff()');
			if (unitText === null) return EMPTY;
			const unit = unitText.trim().toLowerCase();
			if (a.t === 'time' || b.t === 'time') {
				if (a.t !== 'time' || b.t !== 'time') { rt.warn('dateDiff(): a time and a date cannot be measured against each other'); return EMPTY; }
				const seconds = CLOCK_UNIT_SECONDS[unit];
				if (!seconds) { rt.warn(CLOCK_UNIT_ERROR); return EMPTY; }
				const diff = secondsOfDay(a) - secondsOfDay(b);
				return Number.isFinite(diff) ? num(Math.trunc(diff / seconds)) : EMPTY;
			}
			const x = msOf(a);
			const y = msOf(b);
			if (!Number.isFinite(x) || !Number.isFinite(y)) return EMPTY;
			if (unit === 'd') return num(Math.trunc((x - y) / DAY_MS));
			if (unit === 'w') return num(Math.trunc((x - y) / (7 * DAY_MS)));
			if (unit === 'm') return num(wholeMonthsBetween(x, y));
			if (unit === 'y') return num(Math.trunc(wholeMonthsBetween(x, y) / 12));
			if (CLOCK_UNIT_SECONDS[unit]) {
				// BOTH sides must carry a clock. A plain `date` has none, so
				// "hours between two dates" would be hours between two implied
				// midnights — and `dateDiff({{shipped}}, {{ordered}}, "h")` on
				// two date elements is a shape that already exists in stored
				// cards, where it has always warned and blanked. Answering it
				// now would write a number into every one of those items on
				// its next recompute, unasked. Same rule dateAdd applies: a
				// date is moved and measured in calendar units.
				if (a.t !== 'datetime' || b.t !== 'datetime') { rt.warn('dateDiff(): a date has no clock — "h", "min" and "s" need two date-times'); return EMPTY; }
				return num(Math.trunc((x - y) / (CLOCK_UNIT_SECONDS[unit] * 1000)));
			}
			rt.warn(`dateDiff(): unknown unit "${unit.slice(0, 40)}"`);
			return EMPTY;
		},
	},
	dateadd: {
		group: 'Date', args: ['date', 'count', 'unit'], description: 'Moves a date by N units ("d", "w", "m", "y"), or a time or date-time by "h", "min" or "s"; months clamp at month end.', example: 'dateAdd({{purchased}}, 1, "y")',
		params: [P.element('date', DATE_OR_CLOCK), P.number('count', ['number']), P.choice('unit', DATE_PART_CHOICES)],
		check: (a, ctx) => {
			if (a.length !== 3) ctx.fail('dateAdd() takes a date or a time, a count and a unit ("d", "w", "m", "y", "h", "min" or "s").');
			const clock = isClockish(a[0]);
			const d = clock ? a[0] : expectDate(a[0], ctx, 'dateAdd()');
			expectNumber(a[1], ctx, 'dateAdd()'); expectText(a[2], ctx, 'dateAdd()');
			const literal = ctx.literalText(2);
			const unit = literal === null ? null : literal.trim().toLowerCase();
			// A clock FIRST argument is new in this release — before it,
			// {{a time element}} analysed as text and expectDate refused it,
			// so no stored formula can reach this line. A static refusal here
			// cannot break anything that already exists.
			if (clock && unit !== null && !CLOCK_UNITS.includes(unit)) ctx.fail('dateAdd() on a time takes "h", "min" or "s" — a clock has no calendar.');
			// `dateAdd({{a date}}, 12, "h")` is NOT refused here, though a date
			// has no clock to move. That shape has always analysed cleanly (to
			// `date`) and has always warned and blanked at runtime, so a
			// stored config carries it; failing analysis would not even reach
			// the pre-write 400 (validateDrivenConfigs exempts unchanged
			// configs) — it would retire the rule after the write and leave
			// the element frozen at its last value. The apply() branch below
			// keeps the warn-and-blank, which is the behaviour every formula
			// written before today was validated against.
			return d.type === 'empty' ? T.date() : d;
		},
		apply: ([d, n, u], rt) => {
			if (isEmptyValue(d) || isEmptyValue(n) || isEmptyValue(u)) return EMPTY;
			const unitText = scanned(u, rt, 'dateAdd()');
			if (unitText === null) return EMPTY;
			const unit = unitText.trim().toLowerCase();
			if (d.t === 'time') {
				const seconds = CLOCK_UNIT_SECONDS[unit];
				if (!seconds) { rt.warn('dateAdd() on a time takes "h", "min" or "s" — a clock has no calendar.'); return EMPTY; }
				return timeValue(secondsOfDay(d) + Math.trunc(n.v) * seconds);
			}
			const ms = msOf(d);
			if (!Number.isFinite(ms)) return EMPTY;
			if (CLOCK_UNIT_SECONDS[unit]) {
				if (d.t === 'date') { rt.warn('dateAdd(): a date has no clock'); return EMPTY; }
				const moved = ms + Math.trunc(n.v) * CLOCK_UNIT_SECONDS[unit] * 1000;
				if (!Number.isFinite(moved) || Math.abs(moved) > MAX_DATE_MS) { rt.warn('dateAdd(): the date is out of range'); return EMPTY; }
				return dateValue(moved, d.t);
			}
			if (!CALENDAR_UNITS.includes(unit)) { rt.warn(`dateAdd(): unknown unit "${unit.slice(0, 40)}"`); return EMPTY; }
			const out = addUnit(ms, Math.trunc(n.v), unit);
			if (!Number.isFinite(out) || Math.abs(out) > MAX_DATE_MS) { rt.warn('dateAdd(): the date is out of range'); return EMPTY; }
			return dateValue(out, d.t);
		},
	},
	age: {
		group: 'Date', args: ['birth date', 'as of?'], description: 'Whole years since a date (as of today unless given).', example: 'age({{dob}})',
		params: [P.element('birth date', DATEISH), P.element('as of', DATEISH, { optional: true })],
		check: (a, ctx) => {
			if (a.length < 1 || a.length > 2) ctx.fail('age() takes a date and an optional as-of date.');
			expectDate(a[0], ctx, 'age()'); if (a[1]) expectDate(a[1], ctx, 'age()');
			return T.number();
		},
		apply: ([d, asOf], rt) => {
			if (isEmptyValue(d)) return EMPTY;
			const birth = msOf(d);
			const ref = asOf && !isEmptyValue(asOf) ? msOf(asOf) : rt.now.getTime();
			if (!Number.isFinite(birth) || !Number.isFinite(ref)) return EMPTY;
			return num(Math.trunc(wholeMonthsBetween(ref, birth) / 12));
		},
	},
	year: {
		group: 'Date', args: ['date'], description: 'The year of a date.', example: 'year({{purchased}})',
		params: [P.element('date', DATEISH)],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('year() takes one date.'); expectDate(a[0], ctx, 'year()'); return T.number(); },
		apply: ([d]) => { if (isEmptyValue(d)) return EMPTY; const ms = msOf(d); return Number.isFinite(ms) ? num(new Date(ms).getUTCFullYear()) : EMPTY; },
	},
	month: {
		group: 'Date', args: ['date'], description: 'The month of a date (1–12).', example: 'month({{purchased}})',
		params: [P.element('date', DATEISH)],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('month() takes one date.'); expectDate(a[0], ctx, 'month()'); return T.number(); },
		apply: ([d]) => { if (isEmptyValue(d)) return EMPTY; const ms = msOf(d); return Number.isFinite(ms) ? num(new Date(ms).getUTCMonth() + 1) : EMPTY; },
	},
	day: {
		group: 'Date', args: ['date'], description: 'The day of the month.', example: 'day({{purchased}})',
		params: [P.element('date', DATEISH)],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('day() takes one date.'); expectDate(a[0], ctx, 'day()'); return T.number(); },
		apply: ([d]) => { if (isEmptyValue(d)) return EMPTY; const ms = msOf(d); return Number.isFinite(ms) ? num(new Date(ms).getUTCDate()) : EMPTY; },
	},
	// ------------------------------------------------------------- clock
	//
	// elapsed() is the ONE function that wraps across midnight, and it is the
	// reason `-` does not. A shift that starts at 22:00 and ends at 06:00 is
	// eight hours; a raw subtraction that quietly answered eight would make
	// `{{end}} - {{start}}` and `-({{start}} - {{end}})` disagree and would
	// hide a transposed pair of stamps forever. So subtraction stays signed
	// and honest, and the wrap gets a NAME.
	elapsed: {
		group: 'Date', args: ['from', 'to'], description: 'How long from one time to another, wrapping past midnight; on date-times, the plain signed difference.', example: 'elapsed({{opens}}, {{closes}})',
		params: [P.element('from', CLOCKISH), P.element('to', CLOCKISH)],
		check: (a, ctx) => {
			if (a.length !== 2) ctx.fail('elapsed() takes two times, or two date-times.');
			const ok = t => isClockish(t) || t.type === 'datetime' || t.type === 'empty';
			if (!ok(a[0])) ctx.fail(`elapsed() needs a time or a date-time, not ${typeLabel(a[0])}.`);
			if (!ok(a[1])) ctx.fail(`elapsed() needs a time or a date-time, not ${typeLabel(a[1])}.`);
			if ((isClockish(a[0]) && a[1].type === 'datetime') || (isClockish(a[1]) && a[0].type === 'datetime')) {
				ctx.fail('elapsed() cannot mix a time with a date-time — use dateTime({{date}}, {{time}}) to make both the same.');
			}
			return T.measure(DURATION);
		},
		apply: ([a, b]) => {
			if (isEmptyValue(a) || isEmptyValue(b)) return EMPTY;
			if (a.t === 'time' && b.t === 'time') {
				const from = secondsOfDay(a);
				const to = secondsOfDay(b);
				if (!Number.isFinite(from) || !Number.isFinite(to)) return EMPTY;
				// elapsed(a, a) is ZERO, not 24 h: a zero-length shift is a
				// likelier record than a round-the-clock one, and a full duty
				// day spells itself `elapsed(a, b) + 24 h`.
				let seconds = to - from;
				if (seconds < 0) seconds += DAY_S;
				return durationOf(seconds);
			}
			if (a.t === 'datetime' && b.t === 'datetime') {
				const x = msOf(a);
				const y = msOf(b);
				if (!Number.isFinite(x) || !Number.isFinite(y)) return EMPTY;
				return durationOf((y - x) / 1000);
			}
			return EMPTY;
		},
	},
	time: {
		group: 'Date', args: ['value'], description: 'A wall clock: the UTC clock of a date-time, or a "HH:mm" text. `time(now())` is the clock right now.', example: 'time(now())',
		params: [P.element('value', ['datetime', 'time', 'text'])],
		check: (a, ctx) => {
			if (a.length !== 1) ctx.fail('time() takes one value.');
			const t = a[0];
			if (isClockish(t) || t.type === 'datetime' || t.type === 'text' || t.type === 'empty') return T.time();
			return ctx.fail(`time() needs a date-time or a "HH:mm" text, not ${typeLabel(t)}.`);
		},
		apply: ([v], rt) => {
			if (isEmptyValue(v)) return EMPTY;
			if (v.t === 'time') return v;
			if (v.t === 'datetime') return clockOfDateTime(v);
			if (v.t === 'text') {
				if (!spend(rt, v.v.length, 'time()')) return EMPTY;
				const parsed = parseClock(v.v);
				if (!parsed) { rt.warn(`time(): "${v.v.length > 40 ? `${v.v.slice(0, 40)}…` : v.v}" is not a time`); return EMPTY; }
				return parsed;
			}
			return EMPTY;
		},
	},
	datetime: {
		group: 'Date', args: ['date', 'time'], description: 'A date and a wall clock as one date-time. The clock is read in UTC, the same compromise every date makes.', example: 'dateTime({{due}}, {{opens}})',
		params: [P.element('date', DATEISH), P.element('time', ['time'])],
		check: (a, ctx) => {
			if (a.length !== 2) ctx.fail('dateTime() takes a date and a time.');
			expectDate(a[0], ctx, 'dateTime()'); expectTime(a[1], ctx, 'dateTime()');
			return T.datetime();
		},
		apply: ([d, t]) => {
			if (isEmptyValue(d) || isEmptyValue(t)) return EMPTY;
			const ms = msOf(d);
			const seconds = secondsOfDay(t);
			if (!Number.isFinite(ms) || !Number.isFinite(seconds)) return EMPTY;
			const midnight = parseDate(isoDate(ms));
			return dateValue(midnight + seconds * 1000, 'datetime');
		},
	},
	hour: {
		group: 'Date', args: ['value'], description: 'The hour of a time or a date-time (0–23).', example: 'hour({{opens}})',
		params: [P.element('value', CLOCKISH)],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('hour() takes one time or date-time.'); expectClockPart(a[0], ctx, 'hour()'); return T.number(); },
		apply: ([v]) => clockPart(v, 3600, 24),
	},
	minute: {
		group: 'Date', args: ['value'], description: 'The minute of a time or a date-time (0–59).', example: 'minute({{opens}})',
		params: [P.element('value', CLOCKISH)],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('minute() takes one time or date-time.'); expectClockPart(a[0], ctx, 'minute()'); return T.number(); },
		apply: ([v]) => clockPart(v, 60, 60),
	},
	second: {
		group: 'Date', args: ['value'], description: 'The seconds of a time or a date-time (0–59).', example: 'second({{opens}})',
		params: [P.element('value', CLOCKISH)],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('second() takes one time or date-time.'); expectClockPart(a[0], ctx, 'second()'); return T.number(); },
		apply: ([v]) => clockPart(v, 1, 60),
	},

	weekday: {
		group: 'Date', args: ['date'], description: 'The day of the week (1 = Monday … 7 = Sunday).', example: 'weekday({{due}})',
		params: [P.element('date', DATEISH)],
		check: (a, ctx) => { if (a.length !== 1) ctx.fail('weekday() takes one date.'); expectDate(a[0], ctx, 'weekday()'); return T.number(); },
		apply: ([d]) => { if (isEmptyValue(d)) return EMPTY; const ms = msOf(d); if (!Number.isFinite(ms)) return EMPTY; const w = new Date(ms).getUTCDay(); return num(w === 0 ? 7 : w); },
	},

	// --------------------------------------------------------- measurement
	convert: {
		group: 'Measurement', args: ['measurement', 'unit'], description: 'The measurement as a plain number in the given unit.', example: 'convert({{weight}}, "kg")',
		params: [P.element('measurement', ['measure']), P.unit('unit', { measureOf: 0 })],
		check: (a, ctx) => {
			if (a.length !== 2) ctx.fail('convert() takes a measurement and a unit.');
			if (a[0].type !== 'measure' && a[0].type !== 'empty') ctx.fail(`convert() needs a measurement, not ${typeLabel(a[0])}.`);
			expectText(a[1], ctx, 'convert()');
			ctx.checkUnitLiteral(1, a[0].measure);
			return T.number();
		},
		apply: ([m, u], rt) => {
			if (isEmptyValue(m) || isEmptyValue(u)) return EMPTY;
			const unitText = scanned(u, rt, 'convert()');
			if (unitText === null) return EMPTY;
			// An own unit (KPR-1109) converts to nothing, as a count does not.
			if (isOwnUnitId(m.measure)) { rt.warn(`convert(): ${measureWord(m.measure, rt)} convert to no other unit`); return EMPTY; }
			const unit = rt.units && rt.units.resolveUnit(unitText, m.measure || undefined);
			if (!unit) { rt.warn(`convert(): unknown unit "${unitText.slice(0, 40)}"`); return EMPTY; }
			// A reciprocal unit has no number for a zero base (0 mpg in L/100km).
			let out;
			try { out = rt.units.fromBase(m.base, unit.id); } catch (e) { rt.warn(`convert(): ${e.message}`); return EMPTY; }
			if (!Number.isFinite(out)) { rt.warn(`convert(): no value in "${unit.id}"`); return EMPTY; }
			return num(out);
		},
	},
	measure: {
		group: 'Measurement', args: ['number', 'unit'], description: 'A measurement from a number and a unit.', example: 'measure({{grams}}, "g")',
		params: [P.number('number', ['number']), P.unit('unit')],
		check: (a, ctx) => {
			if (a.length !== 2) ctx.fail('measure() takes a number and a unit.');
			expectNumber(a[0], ctx, 'measure()'); expectText(a[1], ctx, 'measure()');
			const unit = ctx.checkUnitLiteral(1);
			return T.measure(unit ? unit.measure : null);
		},
		apply: ([n, u], rt) => {
			if (isEmptyValue(n) || isEmptyValue(u)) return EMPTY;
			const unitText = scanned(u, rt, 'measure()');
			if (unitText === null) return EMPTY;
			const unit = rt.units && rt.units.resolveUnit(unitText);
			if (!unit) { rt.warn(`measure(): unknown unit "${unitText.slice(0, 40)}"`); return EMPTY; }
			// 0 L/100km has no base: never a measurement of Infinity.
			let base;
			try { base = rt.units.toBase(n.v, unit.id); } catch (e) { rt.warn(`measure(): ${e.message}`); return EMPTY; }
			if (!Number.isFinite(base)) { rt.warn(`measure(): no value for ${n.v} ${unit.id}`); return EMPTY; }
			return measureOf(base, unit.measure, unit.id);
		},
	},
};

/**
 * One choice per registry unit, grouped by measure, in the registry's own
 * order: [{ value: unit id, label, group: measure label, measure: measure id }].
 * `value` is the unit's id, which resolveUnit() reads back to the same unit.
 * Null when no usable units module was injected.
 */
function unitChoices(units) {
	if (!units || !Array.isArray(units.MEASURE_LIST) || typeof units.getUnit !== 'function') return null;
	const out = [];
	for (const measure of units.MEASURE_LIST) {
		for (const id of measure.units || []) {
			const unit = units.getUnit(id);
			if (unit) out.push({ value: unit.id, label: unit.label, group: measure.label, measure: measure.id });
		}
	}
	return out;
}

/** A fresh copy of a function's params: a caller cannot reach the table through it. */
function describeParams(fn, units) {
	return (fn.params || []).map(p => {
		const out = Object.assign({}, p, { accepts: p.accepts.slice() });
		if (p.choices) out.choices = p.choices.map(choice => Object.assign({}, choice));
		else if (p.kind === 'unit') {
			const choices = unitChoices(units);
			if (choices) out.choices = choices;
		}
		return out;
	});
}

function describeOne(key, fn, units) {
	return {
		name: FUNCTION_DISPLAY[key] || key,
		group: fn.group,
		args: fn.args.slice(),
		params: describeParams(fn, units),
		description: fn.description,
		example: fn.example,
	};
}

/**
 * The function reference for a UI:
 * [{ name, group, args, params, description, example }].
 *
 * `args` is the label list an editor prints; `params` describes the same
 * arguments by type (see "typed arguments" above). Hand it
 * `{ units }` — the same units module analyze() takes — and a unit param
 * lists every registry unit in `choices`; without it that param has none.
 */
function describeFunctions(options) {
	const units = options && options.units;
	return Object.entries(FUNCTIONS).map(([key, fn]) => describeOne(key, fn, units));
}

/**
 * One function's reference entry (the same shape describeFunctions() lists),
 * found by name in any case, or null when there is no such function. Never
 * throws: a name that is not text, an empty one, or one that merely exists on
 * every object (`constructor`) is simply unknown.
 */
function describeFunction(name, options) {
	if (typeof name !== 'string') return null;
	const key = name.trim().toLowerCase();
	const fn = functionNamed(key);
	return fn ? describeOne(key, fn, options && options.units) : null;
}

// ---------------------------------------------------------------- analyser

/**
 * Is this literal's spelling a currency code (`5 USD`)? UPPER-CASE three
 * letters that the injected currency module knows. Without the module a
 * code-shaped spelling the units registry cannot read is a money literal we
 * cannot judge, and that is the caller's bug (see currencyModule); one the
 * units registry CAN read (CUP, KGS) keeps its unit meaning there — the only
 * reading available without the registry.
 */
const CURRENCY_CODE_RE = /^[A-Z]{3}$/;
function isMoneySpelling(spelling, holder) {
	if (!CURRENCY_CODE_RE.test(spelling)) return false;
	if (holder && holder.currency) return currencyModule(holder).isCurrencyCode(spelling);
	if (holder && holder.units && holder.units.resolveUnit(spelling)) return false;
	currencyModule(holder); // throws
	return false;
}

/**
 * analyze(ast, { resolveRef, units, currency }) -> { type, refs, timeDependent }
 *
 * `resolveRef(path)` returns the static type of a reference (a describeElement
 * result — `list: true` for a multi-value walk), or null when it does not
 * exist. Walk legality (how many hops, which lookups) is the caller's rule.
 * `units` validates unit literals. `currency` (the injected currency module)
 * recognises money literals; it is optional until a formula meets money,
 * and then its absence is a thrown Error.
 *
 * `entry` (KPR-686): `{ group, columns }` analyses a repeating group COLUMN's
 * own formula — every reference is read in one entry's scope (the header).
 * `columns` maps the group's column names to their types (the formula's own
 * column left out by the caller, which says what a self-reference is).
 * `ambiguity`: 'refuse' (default) refuses a name that is both a column and
 * the item's element; 'column' reads the column (a stored formula that a
 * later element came to shadow keeps meaning what it meant).
 *
 * `refs` entries read in an entry scope carry `column: true` and `group`.
 * Throws FormulaError with the offending position.
 */
function analyze(ast, { resolveRef, units, currency, entry = null, ambiguity = 'refuse' } = {}) {
	if (typeof resolveRef !== 'function') throw new Error('analyze needs resolveRef(path).');
	if (!units || typeof units.resolveUnit !== 'function') throw new Error('analyze needs the units registry.');
	const holder = { units, currency: currency || null };
	const refs = [];
	// The entry scopes a reference is read in, innermost last (at most one).
	const scopes = [];
	if (entry) scopes.push({ group: entry.group || null, columns: entry.columns || Object.create(null) });
	const scopeNow = () => (scopes.length ? scopes[scopes.length - 1] : null);

	/**
	 * A reference's type, read in the scope it sits in: an entry's column
	 * first, else the caller's resolver (the header). `{ type, column }`.
	 */
	function resolveIn(path, node) {
		const scope = scopeNow();
		if (!scope) return { type: resolveRef(path.slice()), column: false };
		const colT = columnOf(scope.columns, path[0]);
		if (path.length === 1) {
			if (!colT) return { type: resolveRef(path.slice()), column: false };
			if (ambiguity !== 'column') {
				let outer = null;
				try { outer = resolveRef(path.slice()); } catch (e) { outer = null; }
				if (outer) fail(`{{${path[0]}}} is both a column of ${scope.group || 'the group'} and an element of the item — rename one of them so a formula can tell which it reads.`, posOf(node));
			}
			return { type: colT, column: true };
		}
		if (colT) fail(`{{${path.join('.')}}} follows the column "${path[0]}" with a dot — a column holds its own value: read it as {{${path[0]}}}.`, posOf(node));
		return { type: resolveRef(path.slice()), column: false };
	}

	const ctxFor = (node, argNodes = []) => ({
		pos: posOf(node),
		units,
		fail: (message) => fail(message, posOf(node)),
		isAffine: (measure) => isAffineMeasure(measure, units),
		affineName: (measure) => affineName(measure, units),
		// The affine measure a measure type may be in -- any of its `measures` (KPR-1085).
		affineMeasureOf: (t) => (t && t.type === 'measure' ? (measureSetOf(t) || []).filter(m => isAffineMeasure(m, units))[0] || null : null),
		// The raw text of a literal string argument, or null when the argument
		// is computed. dateDiff/dateAdd judge their unit word with it; a
		// computed unit can only be caught at runtime (rt.warn + blank).
		literalText: (index) => {
			const argNode = argNodes[index];
			return argNode && argNode.type === 'str' ? argNode.value : null;
		},
		// Only a literal string arg can be checked statically.
		checkUnitLiteral: (index, measure) => {
			const argNode = argNodes[index];
			if (!argNode || argNode.type !== 'str') return null;
			const unit = units.resolveUnit(argNode.value, measure || undefined);
			if (!unit) fail(measure ? `"${argNode.value}" is not a unit of ${measure}.` : `"${argNode.value}" is not a known unit.`, posOf(argNode));
			return unit;
		},
	});

	function visit(node) {
		switch (node.type) {
			case 'num': return T.number();
			case 'str': {
				// A date-shaped literal ("2026-01-31", or an ISO timestamp) is a
				// date wherever a date is wanted: age({{dob}}, "2026-01-01").
				if (DATE_RE.test(node.value) && Number.isFinite(parseDate(node.value))) return T.date();
				if (/^\d{4}-\d{2}-\d{2}[T ]/.test(node.value) && Number.isFinite(parseDateTime(node.value))) return T.datetime();
				return T.text();
			}
			case 'bool': return T.bool();
			case 'measure': {
				if (isMoneySpelling(node.unit, holder)) return T.money([node.unit]);
				const unit = units.resolveUnit(node.unit);
				if (!unit) {
					// `5 usd` is a unit spelling nobody has; say what was meant.
					if (currency && currency.isCurrencyCode(node.unit.toUpperCase())) {
						fail(`"${node.unit}" is not a known unit — a currency is written in capitals (5 ${node.unit.toUpperCase()}).`, posOf(node));
					}
					fail(`"${node.unit}" is not a known unit.`, posOf(node));
				}
				return T.measure(unit.measure);
			}
			case 'ref': {
				const { type: t, column } = resolveIn(node.path, node);
				if (!t) {
					fail(node.path.length > 1
						? `{{${node.path.join('.')}}} does not exist — "${node.path[0]}" must be a card lookup with an element "${node.path[1]}".`
						: `{{${node.path[0]}}} does not exist on this card.`, posOf(node));
				}
				if (t.unreadable) {
					fail(t.dataType === 'confirmations'
						? `{{${node.path.join('.')}}} counts confirmations, which change without the item being saved — a formula cannot read one.`
						: `{{${node.path.join('.')}}} is a file — a formula cannot read one.`, posOf(node));
				}
				refs.push(column ? { path: node.path.slice(), type: t, column: true, group: scopeNow().group } : { path: node.path.slice(), type: t });
				if (t.type === 'money' || (t.type === 'list' && t.item && t.item.type === 'money')) currencyModule(holder);
				return t;
			}
			case 'unary': {
				const t = visit(node.arg);
				if (t.type === 'entries') fail(entriesMisused(`"${node.op}"`, t), posOf(node));
				if (node.op === 'not') {
					if (t.type !== 'bool' && t.type !== 'empty') fail(`"not" needs a true/false value, not ${typeLabel(t)}.`, posOf(node));
					return T.bool();
				}
				if (t.type !== 'number' && t.type !== 'measure' && t.type !== 'money' && t.type !== 'empty') fail(`Only numbers and measurements can be negated, not ${typeLabel(t)}.`, posOf(node));
				return t;
			}
			case 'call': return visitCall(node);
			case 'binary': return visitBinary(node);
			default: return fail(`Unknown node "${node.type}".`, posOf(node));
		}
	}

	function visitCall(node) {
		const fn = functionNamed(node.name);
		if (!fn) fail(`Unknown function "${node.name}()".`, posOf(node));
		if (fn.scoped && node.args.length === 2) return visitScoped(node);
		const filtered = filteredCallOf(node);
		if (filtered && !scopeNow()) return visitFiltered(node, filtered);
		wholeGroupTotal(node);
		const types = filtered ? valuesInsideAnEntry(node) : node.args.map(arg => visit(arg));
		// A group's ENTRIES are an argument of count() and isEmpty() alone (any()
		// and all() take them with a condition, above): every other function
		// would read them as a value they are not (KPR-686).
		if (node.name !== 'count' && node.name !== 'isempty' && !fn.scoped) {
			const at = types.findIndex(t => t && t.type === 'entries');
			if (at !== -1) fail(entriesMisused(`${FUNCTION_DISPLAY[node.name] || node.name}()`, types[at]), posOf(node.args[at]));
		}
		return fn.check(types, ctxFor(node, node.args));
	}

	/**
	 * any() / all() over a group's entries (KPR-686): the first argument is the
	 * group, written {{group}}; the condition is analysed in ONE entry's scope
	 * and must be a yes/no. A scope never opens inside another.
	 */
	function visitScoped(node) {
		const name = `${node.name}()`;
		const first = node.args[0];
		if (scopeNow()) fail(`${name} over a group's entries is written on the item, not inside an entry's own calculation.`, posOf(node));
		if (!first || first.type !== 'ref' || first.path.length !== 1) {
			fail(`${name} with a condition takes a repeating group first, written {{group}}: ${node.name}({{lines}}, {{quantity}} > 10).`, posOf(first || node));
		}
		const g = visit(first);
		if (g.type !== 'entries') fail(`${name} with a condition takes a repeating group first; {{${first.path[0]}}} is ${typeLabel(g)}.`, posOf(first));
		scopes.push({ group: g.group || first.path[0], columns: g.columns || Object.create(null) });
		let c;
		try { c = visit(node.args[1]); } finally { scopes.pop(); }
		if (c.type !== 'bool' && c.type !== 'empty') fail(`${name}'s condition must be true or false for each entry, not ${typeLabel(c)}.`, posOf(node.args[1]));
		return T.bool();
	}

	/**
	 * The group a two-argument sum/avg/min/max/count asks about entry by entry
	 * (KPR-1052, entryCallOf): `{ group, column, condition, entries }` when the
	 * first argument names a repeating group and the condition names one of its
	 * columns, else null (the call keeps its meaning: two values). The group's
	 * type is the caller's resolver's, read without recording a reference —
	 * the first argument's own visit records it.
	 */
	function filteredCallOf(node) {
		const call = entryCallOf(node);
		if (!call || !call.aggregate) return null;
		let g = null;
		try { g = resolveRef([call.group]); } catch (e) { g = null; }
		if (!g || g.type !== 'entries' || !conditionNamesColumn(call.condition, g.columns)) return null;
		return { group: g.group || call.group, column: call.column, condition: call.condition, entries: g };
	}

	/**
	 * Inside an entry (a column's own formula, or another condition) no
	 * condition opens (one level): such a call is read as it always was — two
	 * values, `{{amount}} / sum({{line.amount}}, {{amount}})` the entry's own
	 * amount added in — and one whose second value is a yes/no, or reads as
	 * nothing at all there, is the condition it looks like, refused with the
	 * sentence any() says. The evaluator reads it as two values too.
	 */
	function valuesInsideAnEntry(node) {
		const name = `${FUNCTION_DISPLAY[node.name] || node.name}()`;
		const mark = refs.length;
		let types = null;
		try { types = node.args.map(arg => visit(arg)); } catch (e) { types = null; }
		if (!types || types[1].type === 'bool') {
			refs.length = mark;
			fail(`${name} with a condition is written on the item, not inside an entry's own calculation or another condition.`, posOf(node));
		}
		return types;
	}

	/**
	 * sum({{group}}, condition) — avg, min and max the same — means a column
	 * and left it out (KPR-1052): say which shape is meant, naming the group,
	 * before the condition's columns are read as the item's missing elements.
	 */
	function wholeGroupTotal(node) {
		if (node.args.length !== 2 || node.name === 'count' || !ENTRY_AGGREGATES.has(node.name)) return;
		const first = node.args[0];
		if (!first || first.type !== 'ref' || first.path.length !== 1) return;
		let g = null;
		try { g = resolveRef([first.path[0]]); } catch (e) { g = null; }
		if (!g || g.type !== 'entries' || !conditionNamesColumn(node.args[1], g.columns)) return;
		const name = FUNCTION_DISPLAY[node.name] || node.name;
		fail(`${name}() with a condition reads one column of ${first.path[0]}: ${name}({{${first.path[0]}.<column>}}, condition).`, posOf(first));
	}

	/**
	 * sum/avg/min/max({{group.column}}, condition) and count({{group}} or
	 * {{group.column}}, condition) (KPR-1052): the condition is analysed in ONE
	 * entry's scope and must be a yes/no; the result is the type the function
	 * gives the whole column. Every refusal names the group, and the column
	 * when there is one.
	 */
	function visitFiltered(node, call) {
		const name = `${FUNCTION_DISPLAY[node.name] || node.name}()`;
		const first = node.args[0];
		const g = call.group;
		let types;
		if (call.column === null) {
			// count({{group}}, condition): the entries, as count() reads them.
			visit(first);
			types = [T.number()];
		} else {
			const colT = columnOf(call.entries.columns, call.column);
			if (!colT) fail(`{{${g}.${call.column}}} does not exist — ${g} has no column "${call.column}".`, posOf(first));
			const t = visit(first);
			if (node.name !== 'count') {
				const item = t && t.type === 'list' ? t.item : t;
				if (item && item.type !== 'number' && item.type !== 'measure' && item.type !== 'money' && item.type !== 'empty') {
					fail(`${name} over the entries of ${g} reads a column of numbers, measurements or money; "${call.column}" is ${typeLabel(item)}.`, posOf(first));
				}
			}
			types = [t];
		}
		scopes.push({ group: g, columns: call.entries.columns || Object.create(null) });
		let c;
		try { c = visit(call.condition); } finally { scopes.pop(); }
		if (c.type !== 'bool' && c.type !== 'empty') fail(`${name}'s condition must be true or false for each entry of ${g}, not ${typeLabel(c)}.`, posOf(call.condition));
		if (call.column === null) return T.number();
		return functionNamed(node.name).check(types, ctxFor(node, node.args.slice(0, 1)));
	}

	function visitBinary(node) {
		const l = visit(node.left);
		const r = visit(node.right);
		const pos = posOf(node);
		const op = node.op;
		const lt = l.type;
		const rt = r.type;
		const anyEmpty = lt === 'empty' || rt === 'empty';

		if (lt === 'entries' || rt === 'entries') fail(entriesMisused(`"${op}"`, lt === 'entries' ? l : r), pos);
		if (lt === 'list' || rt === 'list') {
			fail(`A list cannot be used with "${op}" — wrap it in sum(), avg(), min(), max() or count().`, pos);
		}

		if (op === 'and' || op === 'or') {
			if ((lt !== 'bool' && lt !== 'empty') || (rt !== 'bool' && rt !== 'empty')) fail(`"${op}" needs true/false values on both sides.`, pos);
			return T.bool();
		}
		if (op === '&') return T.text();
		if (CMP_OPS.has(op)) {
			if (anyEmpty) return T.bool();
			if (isDateish(l) && isDateish(r)) return T.bool();
			// Two clocks order by SECONDS OF DAY, with all six operators.
			if (isClockish(l) && isClockish(r)) return T.bool();
			// `{{opens}} = "09:00"` is how every formula written before `time`
			// had a type of its own spells it. It keeps working, with = and !=
			// only — the same latitude a date already has against text.
			if ((lt === 'text' && (isDateish(r) || isClockish(r))) || (rt === 'text' && (isDateish(l) || isClockish(l)))) {
				if (op !== '=' && op !== '!=') fail('Text can only be compared with = or !=.', pos);
				return T.bool();
			}
			if (lt === 'lookup' && rt === 'lookup') {
				if (op !== '=' && op !== '!=') fail('Lookups can only be compared with = or !=.', pos);
				return T.bool();
			}
			if (!sameType(l, r)) fail(`Cannot compare ${typeLabel(l)} with ${typeLabel(r)}.`, pos);
			if (lt === 'text' && op !== '=' && op !== '!=') fail('Text can only be compared with = or !=.', pos);
			if (lt === 'bool' && op !== '=' && op !== '!=') fail('True/false values can only be compared with = or !=.', pos);
			return T.bool();
		}

		const numberLike = t => t.type === 'number' || t.type === 'measure' || t.type === 'money' || t.type === 'empty';
		// The affine measure a type may be in (KPR-1085: any of its `measures`).
		const affineOf = t => (t.type !== 'measure' ? null : (measureSetOf(t) || []).filter(m => isAffineMeasure(m, units))[0] || null);
		const affine = t => !!affineOf(t);
		const isMoney = t => t.type === 'money';
		// Money never meets a measurement, whatever the operator: a price
		// per kilogram is a formula over two elements, not a unit.
		if ((isMoney(l) && rt === 'measure') || (isMoney(r) && lt === 'measure')) fail('Money and a measurement cannot be combined.', pos);
		const moneyCode = t => t.currency || (t.currencies && t.currencies[0]) || 'USD';
		if (op === '+' || op === '-') {
			if (op === '+' && (affine(l) || affine(r))) {
				const named = affineName(affineOf(l) || affineOf(r), units);
				fail(named.plural === 'Temperatures'
					? 'Temperatures cannot be added — subtract them for a difference in degrees.'
					: `${named.plural} cannot be added — subtract them for a difference.`, pos);
			}
			// A measurement that may be in several measures, one of them affine
			// (KPR-1085): its difference is degrees for one value and a quantity
			// for the next, so it has no one type.
			if (op === '-' && ((affine(l) && l.measures) || (affine(r) && r.measures))) {
				fail(`${affineName(affineOf(l) || affineOf(r), units).plural} and other measures cannot be subtracted in one formula — the answer would be degrees for one item and a quantity for the next.`, pos);
			}
			if (op === '-' && affine(l) && affine(r)) return T.number(); // degrees, in the left operand's unit
			// --- the clock rows, BEFORE the date rows: a time is not a date.
			if (isClockish(l) || isClockish(r)) {
				if (isClockish(l) && isClockish(r)) {
					// SIGNED and unwrapped — see elapsed().
					if (op === '-') return T.measure(DURATION);
					fail('Two times cannot be added — subtract them for the time between, or use elapsed().', pos);
				}
				if (isClockish(l) && (isDurationish(r) || rt === 'empty')) return T.time();
				if (op === '+' && (isDurationish(l) || lt === 'empty') && isClockish(r)) return T.time();
				const other = isClockish(l) ? r : l;
				if (isDateish(other)) fail(`A ${typeLabel(other)} and a time do not combine with "${op}" — use dateTime({{date}}, {{time}}).`, pos);
				if (other.type === 'number') fail(`A time moves by a duration, not by a plain number — try {{t}} ${op} 30 min.`, pos);
				if (other.type === 'measure') fail(`A time moves by a duration, not by a ${typeLabel(other)} — minutes are "min" (30 min); "m" means metres.`, pos);
				fail(`A time can only be moved by a duration ("${op}" with ${typeLabel(other)}).`, pos);
			}
			// A DATE-TIME moves by a duration; a plain DATE does not (it has no
			// clock to move), and dateAdd is where a date is moved.
			if (lt === 'datetime' && isDurationish(r)) return l;
			if (op === '+' && isDurationish(l) && rt === 'datetime') return r;
			if (lt === 'date' && isDurationish(r)) fail('A date has no clock — move it with dateAdd({{d}}, n, "d").', pos);
			if (op === '+' && isDurationish(l) && rt === 'date') fail('A date has no clock — move it with dateAdd({{d}}, n, "d").', pos);
			if (isDateish(l) && (rt === 'number' || rt === 'empty')) return l;
			if (op === '+' && (lt === 'number' || lt === 'empty') && isDateish(r)) return r;
			if (op === '-' && isDateish(l) && isDateish(r)) return T.number();
			if (isDateish(l) || isDateish(r)) fail(`Dates can only be moved by a number of days ("${op}" with ${typeLabel(isDateish(l) ? r : l)}).`, pos);
			if (lt === 'text' || rt === 'text') fail(`Text cannot be added — use "&" to join text.`, pos);
			if (!numberLike(l) || !numberLike(r)) fail(`"${op}" needs numbers or measurements, not ${typeLabel(numberLike(l) ? r : l)}.`, pos);
			if (isMoney(l) && isMoney(r)) {
				const meet = currencyMeet(l, r);
				if (meet && !meet.length) fail(`Cannot combine ${typeLabel(l)} with ${typeLabel(r)} — there is no conversion between currencies.`, pos);
				return T.money(meet);
			}
			if (isMoney(l) || isMoney(r)) {
				if (anyEmpty) return isMoney(l) ? l : r;
				fail(`Money and a plain number cannot be added — give the number a currency (5 ${moneyCode(isMoney(l) ? l : r)}).`, pos);
			}
			if (lt === 'measure' && rt === 'measure') {
				if (!sameType(l, r)) fail(`Cannot combine ${typeLabel(l)} with ${typeLabel(r)}.`, pos);
				if (!l.measures && !r.measures) return T.measure(l.measure || r.measure);
				// Several measures (KPR-1085): only a pair in ONE measure has a sum,
				// so the answer may be in the measures both sides share.
				const meet = measureMeet(l, r);
				return meet ? T.measure(null, meet) : T.measure(null);
			}
			if (lt === 'measure' || rt === 'measure') {
				if (anyEmpty) return lt === 'measure' ? l : r;
				fail('A measurement and a plain number cannot be added — give the number a unit (5 kg) or use convert().', pos);
			}
			return T.number();
		}
		if (op === '*' || op === '/') {
			if (affine(l) || affine(r)) fail(`${affineName(affineOf(l) || affineOf(r), units).plural} cannot be scaled with "${op}".`, pos);
		}
		if (op === '*') {
			if (!numberLike(l) || !numberLike(r)) fail(`"*" needs numbers or measurements, not ${typeLabel(numberLike(l) ? r : l)}.`, pos);
			if (lt === 'measure' && rt === 'measure') fail('Two measurements cannot be multiplied.', pos);
			if (isMoney(l) && isMoney(r)) fail('Two amounts of money cannot be multiplied.', pos);
			if (isMoney(l)) return l;
			if (isMoney(r)) return r;
			if (lt === 'measure') return l;
			if (rt === 'measure') return r;
			return T.number();
		}
		if (op === '/') {
			if (!numberLike(l) || !numberLike(r)) fail(`"/" needs numbers or measurements, not ${typeLabel(numberLike(l) ? r : l)}.`, pos);
			if (lt === 'measure' && rt === 'measure') {
				if (!sameType(l, r)) fail(`Cannot divide ${typeLabel(l)} by ${typeLabel(r)}.`, pos);
				return T.number();
			}
			if (rt === 'measure') fail('A number cannot be divided by a measurement.', pos);
			// USD / USD is a ratio; USD / CAD is a question about an exchange
			// rate, which v1 does not have.
			if (isMoney(l) && isMoney(r)) {
				const meet = currencyMeet(l, r);
				if (meet && !meet.length) fail(`Cannot divide ${typeLabel(l)} by ${typeLabel(r)} — there is no conversion between currencies.`, pos);
				return T.number();
			}
			if (isMoney(r)) fail('A number cannot be divided by money.', pos);
			if (isMoney(l)) return l;
			if (lt === 'measure') return l;
			return T.number();
		}
		if (op === '%' || op === '^') {
			if ((lt !== 'number' && lt !== 'empty') || (rt !== 'number' && rt !== 'empty')) fail(`"${op}" needs plain numbers.`, pos);
			return T.number();
		}
		return fail(`Unknown operator "${op}".`, pos);
	}

	const type = visit(ast);
	return { type, refs, timeDependent: isTimeDependent(ast) };
}

// --------------------------------------------------------------- evaluator

/**
 * evaluate(ast, { read, describe, units, currency, now, blanks }) -> { value, warnings }
 *
 *   read(path)      the RAW stored value(s) for a reference — a scalar, or an
 *                   array for a multi-value walk (undefined when absent)
 *   describe(path)  the reference's static type (same answer analyze got)
 *   units           the units registry (measurement arithmetic and text)
 *   currency        the currency module (money: reading, rounding, text);
 *                   optional until the formula meets money
 *   now             Date (one clock per run)
 *   blanks          'zero' (default) | 'empty' — see toTagged()
 *   entry           (KPR-686) `{ columns, value }`: a repeating group COLUMN's
 *                   own formula, read in the scope of one entry — `value` the
 *                   stored entry ({ id, elements }), `columns` its group's
 *                   column types (analyze's `entry.columns`). A column is
 *                   read before an element of the same name (the header).
 *
 * Returns a TAGGED value; `toStored()` turns it into the platform's stored
 * shape. Never throws for data problems — those become warnings + blank.
 */
function evaluate(ast, { read, describe, units, currency, now, blanks, entry = null } = {}) {
	if (typeof read !== 'function' || typeof describe !== 'function') throw new Error('evaluate needs read(path) and describe(path).');
	if (!units || typeof units.resolveUnit !== 'function') throw new Error('evaluate needs the units registry.');
	const warnings = [];
	// The entry scope a reference is read in (the column's own entry, or the
	// entry an any()/all() condition is asking about), innermost last.
	const scopes = [];
	const valuesOfEntry = e => (e && e.elements && typeof e.elements === 'object' && !Array.isArray(e.elements) ? e.elements : {});
	if (entry) scopes.push({ columns: entry.columns || Object.create(null), values: valuesOfEntry(entry.value) });
	const rt = {
		units,
		currency: currency || null,
		now: now instanceof Date ? now : new Date(),
		blanks: blanks === 'empty' ? 'empty' : 'zero',
		// `always`: the overflow warning, recorded past the 20-warning budget
		// (textTooLong, overBudget) — at most one, as nothing runs after it.
		warn: (message, always) => { if (always || warnings.length < 20) warnings.push(message); },
		// Set by textTooLong or overBudget: a text passed MAX_TEXT_LENGTH, or
		// the evaluation passed MAX_WORK, so nothing more is evaluated and the
		// formula's value is blank.
		overflow: false,
		// Characters scanned and list entries materialized so far (MAX_WORK).
		work: 0,
		// Case-folded texts, and contains() answers per folded text and search.
		folded: new Map(),
		searched: new Map(),
		// The own units (KPR-1109) of the elements read so far, by id: their
		// names, for a warning and for a value spelled as text.
		ownUnits: Object.create(null),
	};
	// One read and one tagging per reference path per evaluation: fifty
	// references to a 40,000-entry lookup list are one list, not fifty.
	const tagged = new Map();

	function toTagged(raw, t, inList = false, where = 'a reference') {
		if (!t) return EMPTY;
		// A repeating group's entries (KPR-686): never blank — a group with none
		// is a list of none, which count() reads as 0 and any() as false.
		if (t.type === 'entries') {
			const items = entriesOf(raw);
			if (items.length && !spend(rt, items.length * LIST_ENTRY_COST, where)) return EMPTY;
			return { t: 'entries', items, columns: t.columns || Object.create(null) };
		}
		if (t.type === 'list') {
			const items = Array.isArray(raw) ? raw : (raw === undefined || raw === null || raw === '' ? [] : [raw]);
			if (!spend(rt, items.length * LIST_ENTRY_COST, where)) return EMPTY;
			const out = { t: 'list', items: items.map(item => toTagged(item, t.item, true)), itemType: t.item };
			if (t.fromEntries) out.fromEntries = true;
			return out;
		}
		const blank = raw === undefined || raw === null || raw === '' || (Array.isArray(raw) && !raw.length);
		// A blank ITEM of a list is skipped by the aggregates whatever the
		// blanks mode says — the mode is about the subject's own inputs.
		if (blank) return inList ? EMPTY : blankFor(t);
		switch (t.type) {
			case 'number': {
				const n = typeof raw === 'number' ? raw : Number(raw);
				if (!Number.isFinite(n)) { rt.warn(`"${describeRaw(raw)}" is not a number`); return blankFor(t); }
				return num(n);
			}
			case 'text': {
				const v = t.dataType === 'location' ? locationText(raw) : raw;
				if (!Array.isArray(v)) return txt(v);
				// Several values joined into one text: a copy, charged by what it holds.
				let size = v.length * LIST_ENTRY_COST;
				for (let i = 0; i < v.length; i++) size += String(v[i]).length;
				return spend(rt, size, where) ? txt(v.join(', ')) : EMPTY;
			}
			case 'bool': return bool(raw === true || raw === 'true');
			// An element that holds SEVERAL dates reads as its EARLIEST — the
			// same entry the sort, the primary date and a range bound read it
			// as. A formula over a list is a formula over one value; the
			// aggregate functions are how a list is folded.
			case 'date': {
				const s = String(Array.isArray(raw) ? raw[0] : raw);
				const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
				if (m && Number.isFinite(parseDate(m[1]))) return { t: 'date', v: m[1] };
				rt.warn(`"${s}" is not a date`);
				return EMPTY;
			}
			case 'datetime': {
				const one = Array.isArray(raw) ? raw[0] : raw;
				const ms = parseDateTime(one);
				if (!Number.isFinite(ms)) { rt.warn(`"${describeRaw(one)}" is not a date-time`); return EMPTY; }
				return { t: 'datetime', v: isoDateTime(ms) };
			}
			// A `time` element may still hold a LEGACY ISO timestamp (SCHEMA.md
			// keeps those verbatim; normalizeTimeValues is the cleanup task).
			// utils/dateOptions.js comparable() deliberately refuses to read a
			// wall clock out of an instant — the row records no zone — and so
			// does this. Warn and blank; never guess a clock.
			case 'time': {
				const one = Array.isArray(raw) ? raw[0] : raw;
				const clock = parseClock(one);
				if (!clock) { rt.warn(`"${describeRaw(one)}" is not a time`); return EMPTY; }
				return clock;
			}
			case 'measure': return measureFromRaw(raw, t);
			case 'money': return moneyFromRaw(raw, t);
			case 'lookup': return { t: 'lookup', v: Array.isArray(raw) ? String(raw[0]) : String(raw) };
			default: return EMPTY;
		}
	}

	function blankFor(t) {
		if (rt.blanks === 'empty') return EMPTY;
		// No object spread here: the web vendors this file, and Babel would
		// inject a helper IMPORT for the spread, turning a CommonJS module
		// into an ES module with no default export.
		if (t.type === 'number') return Object.assign(num(0), { blank: true });
		if (t.type === 'text') return Object.assign(txt(''), { blank: true });
		if (t.type === 'measure') return Object.assign(measureOf(0, t.measure || unitMeasureOf(defaultUnitOf(t)), defaultUnitOf(t)), { blank: true });
		// A zero in the element's default currency; commonCurrency() lets it
		// take the other side's code, so a blank never causes a mismatch.
		if (t.type === 'money') {
			const code = defaultCurrencyOf(t);
			return code ? Object.assign(moneyOf(0, code), { blank: true }) : EMPTY;
		}
		// There is NO zero wall clock. 00:00 is midnight — a real value — so
		// seeding it under blanks:'zero' would start every unanswered shift at
		// midnight and the arithmetic would look like data.
		if (t.type === 'time') return EMPTY;
		return EMPTY;
	}
	rt.defaultUnitOf = t => defaultUnitOf(t);

	/** The measure a unit id is in, by the injected registry, or null. */
	function unitMeasureOf(unitId) {
		const unit = unitId && rt.units ? rt.units.getUnit(unitId) : null;
		return unit ? unit.measure : null;
	}

	function defaultUnitOf(t) {
		const options = t.options || {};
		if (rt.units) {
			const unit = rt.units.getUnit(options.defaultUnit);
			if (unit && (!t.measure || unit.measure === t.measure)) return unit.id;
			const measure = rt.units.getMeasure(t.measure);
			if (measure) return measure.canonical;
		}
		return options.defaultUnit || null;
	}

	rt.defaultCurrencyOf = t => defaultCurrencyOf(t);
	function defaultCurrencyOf(t) {
		return t.currency || currencyModule(rt).defaultCodeOf(t.options || {}) || null;
	}

	// The currency module's own tolerant read (a stored { amount, currency },
	// or a bare number / text in the element's default currency — a half-
	// migrated number element), so a formula reads exactly what the list
	// shows. A stored object with a code the element no longer allows still
	// reads: tightening the list never hides a value on record.
	function moneyFromRaw(raw, t) {
		const one = Array.isArray(raw) ? raw[0] : raw;
		const read = currencyModule(rt).readCurrency(one, t.options || {});
		if (!read) { rt.warn(`"${describeRaw(one)}" is not an amount of money`); return EMPTY; }
		const value = moneyOf(read.amount, read.currency);
		// Non-enumerable: only renderText reads it (the element's symbol
		// choice), and it must not ride into deep-equality of tagged values.
		Object.defineProperty(value, 'options', { value: t.options || {}, enumerable: false });
		return value;
	}

	function measureFromRaw(raw, t) {
		const measure = t.measure || null;
		if (typeof raw === 'number' && !Number.isFinite(raw)) { rt.warn('the measurement is not a finite number'); return EMPTY; }
		if (typeof raw === 'number') {
			const unit = defaultUnitOf(t);
			// A bare number is in the default unit, so in ITS measure (KPR-1085:
			// on an element that allows several, the primary).
			const own = measure || unitMeasureOf(unit);
			if (!rt.units || !unit) return measureOf(raw, own, unit);
			try { return measureOf(rt.units.toBase(raw, unit), own, unit); } catch (e) { rt.warn(e.message); return EMPTY; }
		}
		if (raw && typeof raw === 'object' && typeof raw.value === 'number') {
			if (!Number.isFinite(raw.value) || (raw.base !== undefined && !Number.isFinite(raw.base))) { rt.warn('the measurement is not a finite number'); return EMPTY; }
			// One of the element's OWN units (KPR-1109): a measure of one unit,
			// its base the number itself. One the element no longer names reads
			// as nothing, as a value in a measure it no longer allows does.
			if (isOwnUnitId(raw.unit)) {
				const own = ownUnitsOfOptions(t.options).find(entry => entry.id === raw.unit);
				const allowed = measure ? measure === raw.unit : (!Array.isArray(t.measures) || t.measures.indexOf(raw.unit) !== -1);
				if (!own || !allowed) { rt.warn(`"${raw.unit.slice(OWN_PREFIX.length)}" is not one of the element's units`); return EMPTY; }
				// Another element read in this formula names the same id otherwise
				// (a rename on one of them): two units, whatever the id says.
				// The whole answer is blank (rt.ownClash), not only this value: an
				// aggregate skips a blank, so sum() would otherwise answer with
				// whichever element was read first.
				const seen = rt.ownUnits[own.id];
				if (seen && ownNameKeyOf(seen.singular) !== ownNameKeyOf(own.singular)) {
					rt.warn(`${seen.plural} and ${own.plural} cannot be combined — they are two elements' units under one id (${own.id})`);
					rt.ownClash = true;
					return EMPTY;
				}
				if (!seen) rt.ownUnits[own.id] = own;
				return measureOf(typeof raw.base === 'number' ? raw.base : raw.value, own.id, own.id);
			}
			const unitDef = rt.units ? rt.units.getUnit(raw.unit) : null;
			if (rt.units && !unitDef) { rt.warn(`unknown unit "${raw.unit}"`); return EMPTY; }
			if (unitDef && measure && unitDef.measure !== measure) { rt.warn(`"${raw.unit}" is not a ${measure} unit`); return EMPTY; }
			// A value in a measure the element no longer allows (KPR-1085) reads
			// as nothing, as it does everywhere else.
			if (unitDef && !measure && Array.isArray(t.measures) && t.measures.indexOf(unitDef.measure) === -1) {
				rt.warn(`"${raw.unit}" is not a ${measureListWords(t.measures)} unit`);
				return EMPTY;
			}
			const base = typeof raw.base === 'number' ? raw.base : (rt.units ? rt.units.toBase(raw.value, raw.unit) : raw.value);
			return measureOf(base, measure || (unitDef && unitDef.measure) || null, raw.unit);
		}
		if (typeof raw === 'string' && rt.units && typeof rt.units.parseMeasurement === 'function') {
			try {
				const parsed = rt.units.parseMeasurement(raw, t.options || {});
				if (parsed && typeof parsed.value === 'number') return measureFromRaw(parsed, t);
			} catch (e) { /* fall through */ }
		}
		rt.warn(`"${describeRaw(raw)}" is not a measurement`);
		return EMPTY;
	}

	function describeRaw(raw) {
		let text;
		try { text = typeof raw === 'string' ? raw : JSON.stringify(raw); } catch (e) { text = String(raw); }
		text = text === undefined ? String(raw) : text;
		return text.length > 120 ? `${text.slice(0, 120)}…` : text;
	}

	function ev(node) {
		// Past the text cap or the work budget the answer is already blank:
		// evaluating the rest of the tree would only spend time on a value
		// nobody will see.
		if (rt.overflow) return EMPTY;
		switch (node.type) {
			case 'num': return num(node.value);
			case 'str': {
				if (DATE_RE.test(node.value) && Number.isFinite(parseDate(node.value))) return { t: 'date', v: node.value };
				if (/^\d{4}-\d{2}-\d{2}[T ]/.test(node.value) && Number.isFinite(parseDateTime(node.value))) return { t: 'datetime', v: isoDateTime(parseDateTime(node.value)) };
				return txt(node.value);
			}
			case 'bool': return bool(node.value);
			case 'measure': {
				if (isMoneySpelling(node.unit, rt)) {
					// On the literal's decimal TEXT, never value * 100: 19.99 is
					// 1999 minor units, not 1998.9999999999998. 'keep' leaves an
					// over-precise literal (12.555 USD) fractional, like any
					// mid-computation amount; toStored rounds it once.
					const amount = rt.currency.toMinor(node.value, node.unit, 'keep');
					if (amount === null) { rt.warn(`"${serializeNumber(node.value)} ${node.unit}" is not an amount of ${node.unit}`); return EMPTY; }
					return moneyOf(amount, node.unit);
				}
				const unit = rt.units ? rt.units.resolveUnit(node.unit) : null;
				if (!unit) { rt.warn(`unknown unit "${node.unit}"`); return EMPTY; }
				try { return measureOf(rt.units.toBase(node.value, unit.id), unit.measure, unit.id); } catch (e) { rt.warn(e.message); return EMPTY; }
			}
			case 'ref': {
				// An ENTRY's column (KPR-686): read from the entry in scope, never
				// cached — the next entry holds its own value.
				const scope = scopes.length ? scopes[scopes.length - 1] : null;
				const colT = scope ? columnOf(scope.columns, node.path[0]) : null;
				if (colT) {
					if (node.path.length !== 1) { rt.warn(`{{${node.path.join('.')}}} follows a column with a dot`); return EMPTY; }
					const held = scope.values;
					return toTagged(Object.prototype.hasOwnProperty.call(held, node.path[0]) ? held[node.path[0]] : undefined, colT, false, `{{${node.path[0]}}}`);
				}
				// JSON, not a joined string: a reference segment may hold any
				// character but a dot, a NUL included.
				const key = JSON.stringify(node.path);
				if (tagged.has(key)) return tagged.get(key);
				const value = toTagged(read(node.path.slice()), describe(node.path.slice()), false, `{{${node.path.join('.')}}}`);
				tagged.set(key, value);
				return value;
			}
			case 'unary': {
				const v = ev(node.arg);
				if (isEmptyValue(v)) return EMPTY;
				if (node.op === 'not') return bool(!v.v);
				if (v.t === 'money') return moneyOf(-v.amount, v.currency);
				return v.t === 'measure' ? measureOf(-v.base, v.measure, v.unit) : num(-v.v);
			}
			case 'call': {
				const fn = functionNamed(node.name);
				if (!fn) { rt.warn(`unknown function ${node.name}()`); return EMPTY; }
				if (fn.scoped && node.args.length === 2) return evScoped(node);
				// Inside an entry no condition opens: two values (analyze's
				// valuesInsideAnEntry), read by the plain path below.
				const filtered = scopes.length ? null : filteredCallOf(node);
				if (filtered) return evFiltered(node, filtered);
				if (fn.lazy) {
					// if(): only the taken branch is evaluated (a division by zero
					// on the untaken side must not warn).
					const c = ev(node.args[0]);
					return !isEmptyValue(c) && c.v ? ev(node.args[1]) : ev(node.args[2]);
				}
				return fn.apply(node.args.map(ev), rt);
			}
			case 'binary': return evBinary(node);
			default: return EMPTY;
		}
	}

	/**
	 * any() / all() over a group's entries (KPR-686): the condition is read once
	 * per entry, in its scope, each entry charged before it is read; a blank
	 * answer is not a yes. Stops at the first entry that decides.
	 */
	function evScoped(node) {
		const wantAny = node.name === 'any';
		const where = `${node.name}()`;
		const g = ev(node.args[0]);
		if (!g || g.t !== 'entries') return EMPTY;
		if (g.items.length && !spend(rt, g.items.length * LIST_ENTRY_COST, where)) return EMPTY;
		for (let i = 0; i < g.items.length; i++) {
			scopes.push({ columns: g.columns, values: valuesOfEntry(g.items[i]) });
			let c;
			try { c = ev(node.args[1]); } finally { scopes.pop(); }
			if (rt.overflow) return EMPTY;
			const yes = !!c && c.t === 'bool' && c.v === true;
			if (wantAny && yes) return bool(true);
			if (!wantAny && !yes) return bool(false);
		}
		return bool(!wantAny);
	}

	/**
	 * The evaluator's half of analyze's filteredCallOf (KPR-1052): the same
	 * decision, from the same syntax and the group's static type (`describe`
	 * answers what analyze's resolver did).
	 */
	function filteredCallOf(node) {
		const call = entryCallOf(node);
		if (!call || !call.aggregate) return null;
		let g = null;
		try { g = describe([call.group]); } catch (e) { g = null; }
		if (!g || g.type !== 'entries' || !conditionNamesColumn(call.condition, g.columns)) return null;
		return call;
	}

	/**
	 * sum/avg/min/max/count over the entries that meet a condition (KPR-1052):
	 * every entry charged before it is read, the condition read once per entry
	 * in its scope (a blank answer is not a match), and the matching entries'
	 * values folded by the function itself — so a total of no matches is 0 in
	 * the column's own shape, and min, max and avg of none are blank, exactly
	 * as over the whole column. count() of the group counts the matches.
	 */
	function evFiltered(node, call) {
		const where = `${FUNCTION_DISPLAY[node.name] || node.name}()`;
		const g = ev({ type: 'ref', path: [call.group] });
		if (!g || g.t !== 'entries') return EMPTY;
		const colT = call.column === null ? null : columnOf(g.columns, call.column);
		if (call.column !== null && !colT) return EMPTY;
		if (g.items.length && !spend(rt, g.items.length * LIST_ENTRY_COST, where)) return EMPTY;
		const picked = [];
		let matched = 0;
		for (let i = 0; i < g.items.length; i++) {
			const values = valuesOfEntry(g.items[i]);
			scopes.push({ columns: g.columns, values });
			let c;
			try { c = ev(call.condition); } finally { scopes.pop(); }
			if (rt.overflow) return EMPTY;
			if (!c || c.t !== 'bool' || c.v !== true) continue;
			matched++;
			if (colT) picked.push(toTagged(Object.prototype.hasOwnProperty.call(values, call.column) ? values[call.column] : undefined, colT, true, `{{${call.group}.${call.column}}}`));
		}
		if (call.column === null) return num(matched);
		return functionNamed(node.name).apply([{ t: 'list', items: picked, itemType: colT, fromEntries: true }], rt);
	}

	function evBinary(node) {
		const op = node.op;
		if (op === 'and') { const l = ev(node.left); if (!isEmptyValue(l) && !l.v) return bool(false); const r = ev(node.right); return bool(!isEmptyValue(l) && l.v && !isEmptyValue(r) && r.v); }
		if (op === 'or') { const l = ev(node.left); if (!isEmptyValue(l) && l.v) return bool(true); const r = ev(node.right); return bool(!isEmptyValue(r) && r.v); }
		const l = ev(node.left);
		const r = ev(node.right);
		if (op === '&') return joinWithin([l, r], rt, '"&"');
		if (CMP_OPS.has(op)) return compare(op, l, r);
		if (isEmptyValue(l) || isEmptyValue(r)) return EMPTY;

		const dateish = v => v.t === 'date' || v.t === 'datetime';
		const clockish = v => v.t === 'time';
		const durationish = v => v.t === 'measure' && v.measure === DURATION;
		if (op === '+' || op === '-') {
			// MIRRORS analyze/visitBinary's clock rows operator for operator.
			// The two ladders are separate copies on purpose (one is static,
			// one is runtime) and the shared vectors' `types` section is what
			// stops them drifting.
			if (clockish(l) && clockish(r)) {
				if (op !== '-') { rt.warn('two times cannot be added'); return EMPTY; }
				const from = secondsOfDay(r);
				const to = secondsOfDay(l);
				return Number.isFinite(from) && Number.isFinite(to) ? durationOf(to - from) : EMPTY;
			}
			if (clockish(l) && durationish(r)) return timeValue(secondsOfDay(l) + (op === '+' ? 1 : -1) * durationSeconds(r));
			if (op === '+' && durationish(l) && clockish(r)) return timeValue(secondsOfDay(r) + durationSeconds(l));
			if (clockish(l) || clockish(r)) { rt.warn(`cannot apply "${op}" to ${l.t} and ${r.t}`); return EMPTY; }
			if (l.t === 'datetime' && durationish(r)) return dateValue(msOf(l) + (op === '+' ? 1 : -1) * durationSeconds(r) * 1000, 'datetime');
			if (op === '+' && durationish(l) && r.t === 'datetime') return dateValue(msOf(r) + durationSeconds(l) * 1000, 'datetime');
			if (dateish(l) && r.t === 'number') return dateValue(msOf(l) + (op === '+' ? 1 : -1) * r.v * DAY_MS, l.t);
			if (op === '+' && l.t === 'number' && dateish(r)) return dateValue(msOf(r) + l.v * DAY_MS, r.t);
			if (op === '-' && dateish(l) && dateish(r)) {
				const d = (msOf(l) - msOf(r)) / DAY_MS;
				return Number.isFinite(d) ? num(l.t === 'date' && r.t === 'date' ? Math.round(d) : d) : EMPTY;
			}
			if (l.t === 'money' && r.t === 'money') {
				const code = commonCurrency(l, r);
				if (!code) { rt.warn(currencyMismatch(l, r)); return EMPTY; }
				return moneyOf(op === '+' ? l.amount + r.amount : l.amount - r.amount, code);
			}
			if (l.t === 'measure' && r.t === 'measure') {
				// Two measures (an element that allows several, KPR-1085): no sum.
				if (measuresDiffer(l, r)) { rt.warn(measureMismatch(l, r, rt)); return EMPTY; }
				// A blank seed in another measure than the value it meets is that
				// value's zero: the answer is in the value's measure and unit.
				if (l.measure !== r.measure && isBlankSeed(l) && !isBlankSeed(r)) return measureOf(op === '+' ? r.base : -r.base, r.measure, r.unit);
				if (l.measure !== r.measure && isBlankSeed(r) && !isBlankSeed(l)) return measureOf(l.base, l.measure, l.unit);
				if (op === '-' && isAffineMeasure(l.measure, rt.units)) {
					// Degrees in the left operand's unit: 50 °F - 32 °F = 18.
					const unit = l.unit || r.unit;
					try { return num(rt.units.fromBase(l.base, unit) - rt.units.fromBase(r.base, unit)); } catch (e) { return num(l.base - r.base); }
				}
				return measureOf(op === '+' ? l.base + r.base : l.base - r.base, l.measure || r.measure, l.unit || r.unit);
			}
			if (l.t === 'number' && r.t === 'number') return num(op === '+' ? l.v + r.v : l.v - r.v);
			rt.warn(`cannot apply "${op}" to ${l.t} and ${r.t}`);
			return EMPTY;
		}
		if (op === '*') {
			if (l.t === 'measure' && r.t === 'number') return measureOf(l.base * r.v, l.measure, l.unit);
			if (l.t === 'number' && r.t === 'measure') return measureOf(r.base * l.v, r.measure, r.unit);
			if (l.t === 'money' && r.t === 'number') return moneyOf(l.amount * r.v, l.currency);
			if (l.t === 'number' && r.t === 'money') return moneyOf(r.amount * l.v, r.currency);
			if (l.t === 'number' && r.t === 'number') return num(l.v * r.v);
			rt.warn(`cannot multiply ${l.t} and ${r.t}`);
			return EMPTY;
		}
		if (op === '/') {
			const divisor = r.t === 'measure' ? r.base : (r.t === 'money' ? r.amount : r.v);
			if (divisor === 0) { rt.warn('division by zero'); return EMPTY; }
			if (l.t === 'money' && r.t === 'money') {
				if (!commonCurrency(l, r)) { rt.warn(currencyMismatch(l, r)); return EMPTY; }
				return num(l.amount / r.amount);
			}
			if (l.t === 'money' && r.t === 'number') return moneyOf(l.amount / r.v, l.currency);
			if (l.t === 'measure' && r.t === 'measure') {
				if (measuresDiffer(l, r)) { rt.warn(measureMismatch(l, r, rt)); return EMPTY; }
				return num(l.base / r.base);
			}
			if (l.t === 'measure' && r.t === 'number') return measureOf(l.base / r.v, l.measure, l.unit);
			if (l.t === 'number' && r.t === 'number') return num(l.v / r.v);
			rt.warn(`cannot divide ${l.t} by ${r.t}`);
			return EMPTY;
		}
		if (op === '%') {
			if (l.t !== 'number' || r.t !== 'number') return EMPTY;
			if (r.v === 0) { rt.warn('division by zero'); return EMPTY; }
			return num(l.v % r.v);
		}
		if (op === '^') {
			if (l.t !== 'number' || r.t !== 'number') return EMPTY;
			const out = l.v ** r.v;
			if (!Number.isFinite(out)) { rt.warn('the power is not a finite number'); return EMPTY; }
			return num(out);
		}
		return EMPTY;
	}

	function compare(op, l, r) {
		const le = isEmptyValue(l) || (l.t === 'text' && l.v === '');
		const re = isEmptyValue(r) || (r.t === 'text' && r.v === '');
		if (le || re) {
			if (op === '=') return bool(le && re);
			if (op === '!=') return bool(!(le && re));
			return bool(false);
		}
		let a;
		let b;
		if (l.t === 'money' && r.t === 'money') {
			// Different currencies have no order (v1 has no rates): the
			// answer is blank, not a false that reads like data.
			if (!commonCurrency(l, r)) { rt.warn(currencyMismatch(l, r)); return EMPTY; }
			a = l.amount; b = r.amount;
		} else if (l.t === 'measure' && r.t === 'measure') {
			// Different measures have no order (KPR-1085): blank, not a false.
			if (measuresDiffer(l, r)) { rt.warn(measureMismatch(l, r, rt)); return EMPTY; }
			a = l.base; b = r.base;
		}
		else if ((l.t === 'date' || l.t === 'datetime') && (r.t === 'date' || r.t === 'datetime')) { a = msOf(l); b = msOf(r); }
		// NUMERICALLY, by seconds of day. Both 'HH:mm' and 'HH:mm:ss' occur in
		// stored data, and '09:30' > '09:30:00' as strings.
		else if (l.t === 'time' && r.t === 'time') { a = secondsOfDay(l); b = secondsOfDay(r); }
		else if (l.t === 'number' && r.t === 'number') { a = l.v; b = r.v; }
		else if (l.t === 'text' && r.t === 'text') {
			a = folded(l.v, rt, `"${op}"`);
			b = a === null ? null : folded(r.v, rt, `"${op}"`);
			// The comparison reads up to the shorter of the two.
			if (b === null || !spend(rt, Math.min(a.length, b.length), `"${op}"`)) return EMPTY;
		}
		else if (l.t === 'bool' && r.t === 'bool') { a = l.v ? 1 : 0; b = r.v ? 1 : 0; }
		else if (l.t === 'lookup' && r.t === 'lookup') { a = l.v; b = r.v; }
		else {
			a = renderText(l, rt); b = renderText(r, rt);
			if (!spend(rt, a.length + b.length, `"${op}"`)) return EMPTY;
		}
		const eps = typeof a === 'number' ? 1e-9 : 0;
		switch (op) {
			case '=': return bool(typeof a === 'number' ? Math.abs(a - b) <= eps : a === b);
			case '!=': return bool(typeof a === 'number' ? Math.abs(a - b) > eps : a !== b);
			case '<': return bool(a < b);
			case '<=': return bool(a <= b);
			case '>': return bool(a > b);
			case '>=': return bool(a >= b);
			default: return bool(false);
		}
	}

	let value;
	try {
		value = ev(ast);
	} catch (e) {
		rt.warn(`evaluation failed: ${e && e.message ? e.message : e}`);
		value = EMPTY;
	}
	// A text passed the cap, or the evaluation the work budget, somewhere
	// inside: the formula has no value, even where a function above it would
	// have turned the blank into a number or a flag (len() -> 0, isEmpty() ->
	// true).
	if (rt.overflow) value = EMPTY;
	// Two elements' own units under one id named apart (KPR-1109): no answer.
	if (rt.ownClash) value = EMPTY;
	// An answer in an own unit carries the unit as its element names it, so
	// toStored can hold it to a target's unit of the same id AND name.
	if (value && value.t === 'measure' && isOwnUnitId(value.measure) && rt.ownUnits[value.measure]) value = Object.assign({}, value, { own: rt.ownUnits[value.measure] });
	return { value, warnings };
}

/**
 * A tagged value in the platform's stored shape: number, string, 'YYYY-MM-DD',
 * ISO timestamp, a real boolean for the `boolean` dataType, { value, unit,
 * base } for measurements (in `unit`, or the value's own preferred unit),
 * { amount, currency } for money, '' for blank. Lists are not storable — the
 * analyser refuses them as a final result.
 *
 * Money is rounded HERE and nowhere earlier: half away from zero to whole
 * minor units (the currency's exponent), the platform's roundHalfAway. The
 * code rides on the value, so no registry is needed. Whether the TARGET
 * element allows that currency is the caller's question (the driven write
 * bypasses the row validator; utils/automations/formulaFields.js asks it).
 */
function toStored(value, { units, unit, measures, ownUnits } = {}) {
	if (!value || value.t === 'empty') return '';
	switch (value.t) {
		case 'number': return snap(value.v);
		case 'text': return value.v;
		// Byte-for-byte what utils/dateOptions.js coerceTime writes, and the
		// ONLY guard on the shape: a driven write goes straight to updateOne
		// in utils/automations/drivenFields.js, bypassing the row validator
		// and dateOptions entirely. timeValue() is the single mint.
		case 'date': case 'datetime': case 'time': case 'lookup': return value.v;
		// A `boolean` element stores a real BSON boolean, so a bool result
		// materialises as one; a bool driving a TEXT element passes through
		// the coercion matrix's booleanToText, which spells the element's own
		// captions.
		case 'bool': return value.v === true;
		case 'measure': {
			// An answer in an OWN unit (KPR-1109) lands only in an element that
			// has that unit -- one of its `measures`, which list an element's own
			// unit ids -- as its number and that unit; in a one-measure target
			// (`unit` alone) or any other it is stored as nothing, never its
			// number read in another unit. With no target named it keeps its unit.
			// Given the target's own units (`ownUnits`), its unit of that id must
			// also be NAMED as the answer's is: a target whose own:smidgen was
			// renamed "dollop" is another unit.
			if (isOwnUnitId(value.measure)) {
				if (Array.isArray(measures) ? measures.indexOf(value.measure) === -1 : !!unit) return '';
				if (Array.isArray(ownUnits) && value.own) {
					const into = ownUnitsOfOptions({ ownUnits }).find(own => own.id === value.measure);
					if (!into || ownNameKeyOf(into.singular) !== ownNameKeyOf(value.own.singular)) return '';
				}
				return { value: snapMagnitude(value.base), unit: value.measure, base: snap(value.base) };
			}
			let target = unit || value.unit || (units && units.getMeasure && units.getMeasure(value.measure) && units.getMeasure(value.measure).canonical) || null;
			// A target element that allows SEVERAL measures (`measures`, KPR-1085):
			// an answer in the measure of `unit` (its default unit) lands there;
			// one in another measure it allows keeps the unit it was read in;
			// one in a measure it does not allow is stored as nothing.
			if (Array.isArray(measures) && measures.length > 1 && units && value.measure) {
				if (measures.indexOf(value.measure) === -1) return '';
				const into = target && units.getUnit(target);
				if (!into || into.measure !== value.measure) {
					const own = units.getUnit(value.unit);
					const measure = units.getMeasure(value.measure);
					target = own && own.measure === value.measure ? own.id : (measure ? measure.canonical : null);
					if (!target) return '';
				}
			} else if (unit && units && value.measure) {
				// A one-measure target: an answer in another measure is stored as
				// nothing, never its base read in the target's unit (250 g is no
				// 1.06 cup). A formula written over a one-measure source whose
				// element has since allowed several meets one at run time.
				const into = units.getUnit(unit);
				if (into && into.measure !== value.measure) return '';
			}
			if (!target) return snap(value.base);
			let magnitudeValue = value.base;
			if (units) { try { magnitudeValue = units.fromBase(value.base, target); } catch (e) { return ''; } }
			return { value: snapMagnitude(magnitudeValue), unit: target, base: snap(value.base) };
		}
		case 'money': {
			const amount = minorAmountOf(value);
			return amount === null ? '' : { amount, currency: value.currency };
		}
		case 'list': return '';
		default: return '';
	}
}

/** The result type's platform dataType, for the coercion matrix. */
function dataTypeOf(t) {
	if (!t) return null;
	switch (t.type) {
		case 'number': return 'number';
		case 'text': return 'text-large';
		case 'date': return 'date';
		case 'datetime': return 'date-time';
		case 'time': return 'time';
		case 'measure': return 'measurement';
		case 'money': return 'currency';
		case 'lookup': return 'card-lookup';
		case 'bool': return 'boolean';
		default: return null; // list, empty: not storable
	}
}

module.exports = {
	MAX_FORMULA_LENGTH,
	MAX_REFS,
	MAX_TEXT_LENGTH,
	MAX_WORK,
	FormulaError,
	tokenize,
	parse,
	serialize,
	references,
	scopedReferences,
	entryCallOf,
	isTimeDependent,
	describeElement,
	entriesOf,
	columnValuesOf,
	typeLabel,
	dataTypeOf,
	analyze,
	evaluate,
	toStored,
	renderText,
	describeFunctions,
	describeFunction,
	FUNCTION_NAMES: Object.keys(FUNCTIONS).map(key => FUNCTION_DISPLAY[key] || key),
};
